// This are reducers to add dynamically to the store
import { createSelector, current, PayloadAction } from "@reduxjs/toolkit";
import { getAreaState, getCurrentValue2, getAreaProperty2, getAreaBoxes, getAreaGrid, getAreaFineGrid } from "reduxModules/selectors/areaSelectors";

// Temporary type-safe wrappers for missing functions
// Note: getAreaBoxes is now imported from container.ts
//const getAreaGrid = (state: any, space = 1) => getAreaProperty2(state, 'areaGrid') || { gridSpacing: space, gridXOffset: 0, gridYOffset: 0, gridX: 20, gridY: 20 };
import toolIndex from "toolConstants";
import { closest, findAngle, side, calculateAimingAngles, calculateSpeakerRotation, calcMaxTilesFit } from "utilities/mathematic";
import { calculateCoordinates, getCollisions, getOverlaps, isInside } from "utilities/geometric";
import { clamp, isEqual, mapValues, max, merge, min, set, transform, values, indexOf, findKey, round, toPairs, pick, mapKeys, has, inRange, find, every, some, minBy, sortBy, trimEnd, omit, isEmpty, isNull, floor, mergeWith, isNumber, keys } from "lodash";
import MuiTableConstants from "config/tables/tableConstants";
import { MIC_CATEGORIES, getPrimaryMicType, micInCategory } from "types/models/microphone";
import { familyMicModelName, hasFamilyMicName } from "utilities/micFamily";
import { isDevioUcxDesign, normalizeUcComputeForUcx, UCX_UC_COMPUTE_FALLBACK } from "utilities/treeFrogBundleMatching";

import createCachedSelector from "re-reselect";
import { getDrawingBox, getIsMeter, getScale } from "./ui";
import { getStoreProp, pluralize } from "utilities/string";
import { distanceToString, toFeet } from "utilities/format";
import { log } from "console";
import { calculateWallSpeakerTargetPoint, computeCoverageBoxesForArea, computeMicRadius, resolveCeilingMicDensity } from "reduxModules/calculations/roomCalculations";
import { computeAndroidAutoCamera, PORT_OVERFLOW_FALLBACK_TOAST, SPEAKER_FALLBACK_TOAST } from "utilities/treeFrogPlatform";
import { isDevioUcxEligible, maxExpansionMicCountFor, maxExpansionSpeakerCountFor, isUCXViable, isUCXViableForArea } from "toolConstants/conferenceroomDesigner/treeFrogPorts";
import { calcExpansionCeilingMicPlacement, getExpansionCeilingIsFocused } from "toolConstants/conferenceroomDesigner/logic";
import { resolveBarProductFamily } from "utilities/parleBarBundle";

// Basic types for room state
interface RoomState {
  areas: { [key: string]: any };
  activeTab: string;
  currentTool: string;
}

// Default customisation flags applied to an area when none exist yet. Defined once so the
// reducers that lazily initialize it (addExpansionMic, removeExpansionMic, etc.) stay in
// sync if a new device key joins the customized object.
const DEFAULT_CUSTOMIZED = {
  microphones: false,
  speakers: false,
  cameras: false,
  expansionMicrophones: false,
  expansionSpeakers: false,
} as const;

// Standard Parlé TTM-X table-mic z-height (meters). Matches the value used by the table
// expansion placeholder in roomCalculations.ts so manual and auto placements share the
// same vertical position.
export const TABLE_MIC_Z_HEIGHT = 0.91;

// Fallback ceiling height for the manual ceiling-expansion add path (meters). Matches the
// initial-state default (9'6" → 2.8956 m) so a missing area.ceilingHeight doesn't crash
// the placement; under normal flow ceilingHeight.min is always set.
const DEFAULT_CEILING_HEIGHT_M = 2.8956;

/**
 * Hides the primary "Focus/Full" coverage toggle after a manual move / add / delete by
 * setting `deviceState.hideCoverageToggle` — the menus.js condition reads this flag to
 * hide the toggle, since a hand-edited layout no longer matches either preset box.
 *
 * Deliberately does NOT touch `deviceState.coverage` itself: that field is read directly
 * (as `.focused` / `.full_room`, with no null-guard) throughout roomCalculations.ts and
 * geometric.ts to compute coverage boxes and jump points, so nulling it crashes those
 * calculations the moment a mic/speaker is moved. The undo button (resetArea) clears this
 * flag back to false, which is all that's needed to bring the toggle back — `coverage`
 * was never modified, so it still holds whatever value it had before the manual edit.
 *
 * Also stashes the current `coverageDensity` on `lastCoverageDensity`. Unlike `coverage`,
 * `coverageDensity.value` (the "Coverage density" slider position) genuinely DOES get
 * overwritten by the very next recalculation: `getDeviceLayouts` only preserves the user's
 * slider pick while `!isMicCustom` (see `usesGridDensity` in roomCalculations.ts) — once
 * this manual edit flips `customized.microphones` to true, the next pass resets the slider
 * to the box-appropriate default. Stashing here (before that recalculation runs) is what
 * lets undo put the slider back where the user left it instead of at that default.
 *
 * IMPORTANT: only stash on the FIRST manual edit after a reset/undo (guarded by
 * `!deviceState.hideCoverageToggle`). By the time of a SECOND edit, `deviceState.coverageDensity`
 * has already been overwritten by the recalculation that followed the first edit (per the
 * paragraph above) — re-stashing unconditionally on every edit would clobber the true
 * original slider value in `lastCoverageDensity` with that already-defaulted one, so undo
 * would restore the wrong density after more than one edit.
 */
const clearPrimaryCoverage = (deviceState: any) => {
  if (!deviceState) return;
  if (!deviceState.hideCoverageToggle && deviceState.coverageDensity) {
    deviceState.lastCoverageDensity = deviceState.coverageDensity;
  }
  deviceState.hideCoverageToggle = true;
};

/**
 * Same as `clearPrimaryCoverage` but for the expansion mic/speaker coverage fields, which
 * live flat on the area (`expansionMicCoverage` / `expansionSpeakerCoverage`) rather than
 * nested under a device object. Stashes the previous value on `${coverageKey}LastKnown`.
 */
const clearExpansionCoverage = (area: any, coverageKey: 'expansionMicCoverage' | 'expansionSpeakerCoverage') => {
  const lastKnownKey = `${coverageKey}LastKnown`;
  if (area[coverageKey] !== null && area[coverageKey] !== undefined) {
    area[lastKnownKey] = area[coverageKey];
  }
  area[coverageKey] = null;
};

interface DeviceLocation {
  x: number;
  y: number;
  z?: number;
}

interface Device {
  model: string;
  modelName: string;
  location: DeviceLocation;
  [key: string]: any;
}

/**
 * Helper function to constrain a position to the nearest wall.
 * @param {Object} location - The location to constrain {x, y}.
 * @param {number} roomLength - The room length in meters.
 * @param {number} roomWidth - The room width in meters.
 * @returns {Object} The constrained location {x, y}.
 */
const constrainToWall = (location, roomLength, roomWidth) => {
  const distToLeft = location.x;
  const distToRight = roomWidth - location.x;
  const distToTop = location.y;
  const distToBottom = roomLength - location.y;

  const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom);

  if (minDist === distToLeft) {
    return { x: 0, y: location.y };
  } else if (minDist === distToRight) {
    return { x: roomWidth, y: location.y };
  } else if (minDist === distToTop) {
    return { x: location.x, y: 0 };
  } else {
    return { x: location.x, y: roomLength };
  }
};

/**
 * Helper function to get the mirrored position for a linked speaker pair.
 * @param {Object} location - The location to mirror {x, y}.
 * @param {Object} area - The area object containing room dimensions.
 * @returns {Object} The mirrored location {x, y}.
 */
const getMirroredLocation = (location, area) => {
  // Determine which wall the speaker is on
  const onLeftWall = location.x === 0;
  const onRightWall = location.x === area.width;
  const onTopWall = location.y === 0;
  const onBottomWall = location.y === area.length;

  if (onLeftWall || onRightWall) {
    // Mirror vertically around room center
    const centerY = area.length / 2;
    const offsetFromCenter = location.y - centerY;
    return { x: location.x, y: centerY - offsetFromCenter };
  } else if (onTopWall || onBottomWall) {
    // Mirror horizontally around room center
    const centerX = area.width / 2;
    const offsetFromCenter = location.x - centerX;
    return { x: centerX - offsetFromCenter, y: location.y };
  }

  return location;
};

/** One pole section (0.5 feet) in meters. */
const POLE_INCREMENT_M = 0.1524;

/**
 * Snap a manually-set microphone height to the nearest whole pole section.
 *
 * TCM-X / DCM-X / CBC 2500 PM (MIC_CATEGORIES.POLE_MOUNT) hang from the ceiling on
 * fixed-length pole sections, so only multiples of 0.1524m are physically buildable —
 * a height of e.g. 7'9" cannot be achieved by any combination of sections.
 *
 * Shared by setMicrophones (primary) and setExpansionMicrophones (expansion ceiling mic)
 * so the two paths cannot drift: the expansion path previously clamped to heightRange
 * WITHOUT snapping, so the canvas and the height field showed an unbuildable height while
 * logic.js sized real TCM-X-PM / PMA-2000-PE hardware off it (calculateEquipmentTable's
 * expansion pole-accessory block).
 *
 * The epsilon absorbs float error introduced by the ft→m conversion upstream
 * (toMeter rounds to 4dp) so values already sitting on a section don't drift off it.
 *
 * NOTE: applies to MANUAL height entry only — both the spinner arrows and typed input,
 * which converge on the same `{ height }` payload. Flush-to-ceiling heights derived from
 * a model's own height() formula are deliberately left unsnapped, since a ceiling is not
 * required to sit on the pole grid.
 */
const snapToPoleIncrement = (height: number): number =>
  Math.round(Math.round((height + 0.0001) / POLE_INCREMENT_M) * POLE_INCREMENT_M * 10000) / 10000;

// WDT-90 — Re-evaluate UCX eligibility AND auto-camera after any state change
// that affects mic/speaker count or type. Four branches:
//   - Exclusive (bar + expansion mics and/or expansion speakers, on ANY platform):
//     Devio UCX is the only platform that can carry the design — force
//     platform=Android (snapshotting the prior family the same way setPlatform
//     does) so the design can't be left stranded on Windows.
//   - Overflow (final AC, platform already Android): KEEP platform=Android,
//     switch family to Tesira so logic.js routes peripherals through a Tesira
//     DSP + UCC-UCX1000-T.
//   - Hard ineligibility (BMA360D / pendant, platform already Android): REVERT
//     platform to Windows and restore the pre-Android family snapshot.
//   - Eligible (platform already Android): re-run the auto-camera helper — e.g.
//     user switched from a VBC bar (integrated camera, skip) to ABC 2500a or
//     TCM-X (needs Vidi 280).
// Called from any reducer that mutates mic/speaker state (setMicrophones,
// addDevice, removeDevice, setExpansionMicType, addExpansionMic,
// removeExpansionMic, setExpansionMicrophones, setExpansionSpeakerType,
// addExpansionSpeaker, removeExpansionSpeaker, setExpansionSpeakers).
// PIG-595 (review): the microphone DISPLAY name (`modelName`, shown in the
// Microphones menu and the Microphone-locations tab) must track the selected
// product family. On Devio (Tree Frog / Android) a ceiling TCM-X ships as a DCM-X
// and a table TTM-X as a DTM-X — the equipment list already translates these, but
// the menu/locations read the stored `modelName`, so without this they still said
// "TCM-X". This keeps the stored name in step immediately on a family change; the
// device recalc reinforces it via familyMicModelName in calculateMicPlacement. The
// `model` key stays the stable identity — only the display name changes — so it's
// idempotent and reverts to the Parlé name off Devio.
const applyDevioMicModelName = (area: any) => {
  const mic = area?.microphones;
  if (hasFamilyMicName(mic?.model)) {
    // DCM-X / DTM-X apply to a genuine Devio UCX (Android) design only — NOT to Devio SCX
    // (Devio family on Windows), which keeps the Parlé TCM-X / TTM-X names (PIG-594).
    const targetName = familyMicModelName(mic.model, isDevioUcxDesign(area), mic.modelName);
    if (targetName && mic.modelName !== targetName) {
      mic.modelName = targetName;
      if (Array.isArray(mic.layout)) {
        mic.layout.forEach((device: any) => { if (device) device.modelName = targetName; });
      }
    }
  }

  // Table expansion mics (DTM-X ↔ TTM-X) need the same family-aware
  // correction as the primary mic above — `setExpansionMicType` and the keystone
  // placement algorithm both hardcode the Devio placeholder ('Parlé DTM-X')
  // regardless of family, so switching a bar+ext design off Devio (e.g. a port
  // overflow) left the equipment list and canvas showing DTM-X/DTM-XEX even
  // once productFamily correctly resolved to Tesira. Scoped to table only for
  // now — ceiling expansion mics (DCM-X ↔ TCM-X) have the identical gap but are
  // a deliberately deferred follow-up.
  const expansionMic = area?.expansionMicrophones;
  if (area?.expansionMicType === 'table' && hasFamilyMicName(expansionMic?.model)) {
    const targetExpansionName = familyMicModelName(expansionMic.model, isDevioUcxDesign(area), expansionMic.modelName);
    if (targetExpansionName && expansionMic.modelName !== targetExpansionName) {
      expansionMic.modelName = targetExpansionName;
      if (Array.isArray(expansionMic.layout)) {
        expansionMic.layout.forEach((device: any) => { if (device) device.modelName = targetExpansionName; });
      }
    }
  }
};

/**
 * "Remove bar" (spec §2.2, flowchart node E) — the mechanical side of the
 * Devio UCX capacity fallback for a Bar + expansion design: the expansion
 * mics/speakers (already real, placed devices) are promoted to PRIMARY, and
 * the bar mic itself is dropped, since the fallback target (Tesira-via-USB
 * or Devio SCX/Tesira) has no bar-hub concept — only a standalone
 * ceiling/table primary design.
 *
 * NOTE: bar removal is described only as a flowchart node in the spec, with
 * no data-shape detail — this is a best-effort, most-plausible reading
 * (promote the expansion group, matching model/height; ceiling additionally
 * gets a fresh silent placement rather than a wholesale coordinate copy — see
 * `computeStandaloneCeilingLayout` below) and should be reviewed against
 * `initialState.js`'s real primary mic/speaker shape before shipping,
 * particularly any fields a genuine ceiling/table primary carries that a bar
 * mic's slot didn't.
 */
/**
 * Real-math (DOM-independent) ESTIMATE of how many primary ceiling mics a genuinely
 * standalone ceiling design would need in this room — reuses the SAME density
 * derivation (`getOverlaps` + `resolveCeilingMicDensity`) that `calculateMicPlacement`
 * (roomCalculations.ts) itself performs before it samples the real `#micPath` SVG
 * element for exact coordinates.
 *
 * `calculateMicPlacement` is unavoidably DOM-dependent for exact positions — it reads
 * `document.getElementById('micPath')` for EVERY table shape, not only round/keystone/
 * vshape — so computing a pixel-accurate primary ceiling layout synchronously inside
 * this reducer isn't achievable. An earlier attempt approximated it instead with the
 * ceiling EXPANSION mic's box-grid math (`calcBarExpansionPlacement` with `barReach: 0`)
 * and produced the WRONG count for a real room (7 vs the real algorithm's 14) — that
 * math is a different algorithm, not a DOM-free equivalent of this one.
 *
 * Only the QTY is estimated here (good enough for `applyUcxCapacityFallback`'s
 * immediate Devio↔Tesira mic-count decision) — `promoteExpansionToPrimary` leaves
 * `microphones.layout` EMPTY and sets `state._pendingCeilingPromotionRecalc` so
 * `calculateDevicesMiddleware` schedules a real, DOM-based recalculation pass (the
 * same one a manual Bar→Ceiling switch gets) to fill in accurate positions once React
 * has painted `#micPath` for the newly-promoted primary mic.
 *
 * @param coverage - the primary mic's own resolved `{ focused, full_room }` coverage (computed by
 *   the caller from `expansionMicCoverage`/table presence) — NOT re-derived here, so this always
 *   agrees with the `microphones.coverage` the caller is about to commit.
 */
const estimateStandaloneCeilingMicCount = (
  state: any, area: any, coverage: { focused: boolean; full_room: boolean },
): number => {
  // Defensive fallback, same reasoning as `dryRunCeilingExpansionMicCount` above: without real
  // room geometry there's nothing to compute against.
  if (!area?.dimensions || !area?.location || !area?.table) {
    return area?.expansionMicrophones?.layout?.length ?? 0;
  }
  const constants = toolIndex[state.currentTool].constants;
  const coverageBoxes = computeCoverageBoxesForArea(area, constants);
  const isFocused = Boolean(coverage.focused);
  const micCoverageBox = coverageBoxes[isFocused ? 'focused' : 'full_room'];
  const acousticIndex = indexOf(constants.acoustics, area.acoustics);
  const height = area.expansionMicrophones?.height
    ?? area.ceilingHeight?.min
    ?? area.ceilingHeight
    ?? DEFAULT_CEILING_HEIGHT_M;
  const talkerHeight = constants.area?.talkerHeight ?? 1.2009;
  const micMaxDist = constants.equipment?.microphones?.tcm_x?.micData?.[acousticIndex];
  if (typeof micMaxDist !== 'number') {
    return area?.expansionMicrophones?.layout?.length ?? 0;
  }
  const micRadius = computeMicRadius(micMaxDist, height, talkerHeight, area.acoustics, false);
  const isEndTable = constants.tables?.[area.table?.shape]?.endTable ?? false;
  const micOverlaps = getOverlaps(micCoverageBox, micRadius, coverage, area.table, isEndTable);
  const micCoverageKey = findKey(coverage) ?? (isFocused ? 'focused' : 'full_room');
  return resolveCeilingMicDensity(
    micOverlaps.jumpPoints, micOverlaps.overlaps, [], 0, constants.defOverlap?.[micCoverageKey] ?? 1.6, true,
  );
};

const promoteExpansionToPrimary = (state: any, area: any) => {
  const wasCeiling = area.expansionMicType === 'ceiling';
  const expansionLayout = area.expansionMicrophones?.layout ?? [];
  // Speaker catalog, used by the bar-speaker migration further down. Read defensively (`?? {}`)
  // so a partially-built area / test fixture without a resolvable tool still reduces cleanly —
  // the migration simply doesn't fire rather than throwing mid-promotion.
  const spkrEquipment = toolIndex[state.currentTool]?.constants?.equipment?.speakers ?? {};
  // `area.expansionMicCoverage` (initialState.js:151) is the expansion-side
  // twin of primary `microphones.coverage` — same { focused, full_room }
  // shape, just tracked as a sibling field instead of nested (see
  // roomCalculations.ts:660). Carry the user's actual setting forward
  // rather than inventing one. `null` (never explicitly set) falls back to
  // the exact same default `setExpansionMicType` itself uses on first
  // ceiling selection — focused when the room has a table, full_room
  // otherwise (based on furniture presence, NOT mic type — table
  // expansion's own default-setting path doesn't set this field at all, so
  // this is the only precedent to match).
  const coverage = area.expansionMicCoverage ?? { focused: Boolean(area.table?.shape), full_room: !area.table?.shape };
  // Ceiling: don't carry the expansion mics' old coordinates forward as the new primary layout —
  // they were computed against the EXPANSION box (excluding the bar's own coverage), which no
  // longer applies once the bar is gone. An accurate PIXEL layout can't be computed here without
  // the real `#micPath` DOM element `calculateMicPlacement` needs (see
  // `estimateStandaloneCeilingMicCount`'s doc comment) — leave the layout empty and flag a
  // follow-up recalculation (below) so `calculateDevicesMiddleware` runs the real, DOM-based
  // placement pass for this mic, same as a manual Bar→Ceiling switch would. Only the qty is
  // estimated now, for the immediate Devio↔Tesira decision this function's caller makes right
  // after. Table (ttm_x) keeps the prior wholesale-copy behavior — out of scope here.
  const promotedQty = wasCeiling
    ? estimateStandaloneCeilingMicCount(state, area, coverage)
    : expansionLayout.length;
  area.microphones = {
    ...area.microphones,
    model: wasCeiling ? 'tcm_x' : 'ttm_x',
    modelName: area.expansionMicrophones?.modelName ?? area.microphones?.modelName,
    layout: wasCeiling ? [] : expansionLayout,
    qty: promotedQty,
    height: area.expansionMicrophones?.height,
    coverage,
  };
  if (!area.customized) area.customized = { ...DEFAULT_CUSTOMIZED };
  area.customized.microphones = false;

  const hadExpansionSpeakers = area.expansionSpeakerType === 'ceiling' || area.expansionSpeakerType === 'pendant';
  if (hadExpansionSpeakers) {
    const spkrLayout = area.expansionSpeakers?.layout ?? [];
    // `area.expansionSpeakerCoverage` is the speaker-side twin of `expansionMicCoverage` above,
    // and must be carried forward for the same reason — with one extra consequence that makes it
    // load-bearing rather than cosmetic.
    //
    // Without this the spread below inherits the BAR speaker's coverage, which is hardcoded
    // `{ focused: false, full_room: false }` (constants.js `barspkr`). That all-false shape is
    // only ever valid while a bar is primary, because the Coverage toggle is hidden for bar mics
    // (menus.js `!isBarMic(microphones.model)`). The moment this promotion makes a ceiling/pendant
    // mic primary the toggle becomes visible — rendering with NEITHER option selected — and, worse,
    // `findKey(speakers.coverage)` (roomCalculations.ts, speaker placement) returns `undefined`, so
    // `coverageBoxes[undefined]` is `undefined` and `calculateSpeakerPlacement` bails at its
    // `!coverageBox` guard, returning the existing layout. That layout was just cleared by the
    // `setMicrophones` recalculation dispatch this promotion triggers
    // (`_pendingCeilingPromotionRecalc`, see below), so the design ends up with ZERO speakers.
    //
    // `resetArea`'s own speaker migration (logic.js, case 'microphones') would normally repair the
    // coverage, but it can't here: it keys off `_oldModel`, and this function has already rewritten
    // `microphones.model`, so it sees no type change and skips both of its branches.
    //
    // `null` (the user never touched the expansion Focused/Full toggle) falls back to the same
    // default `setExpansionSpeakerType` seeds on first ceiling/pendant selection — focused when the
    // room has a table, full_room otherwise. That is also exactly what `ceilingspkr.coverage(area)`
    // / `pendantspkr.coverage(area)` return, so a promoted group lands where a fresh selection would.
    const spkrHasTable = Boolean(area.table?.shape);
    const spkrCoverage = area.expansionSpeakerCoverage ?? { focused: spkrHasTable, full_room: !spkrHasTable };
    const promotedSpkrModel = area.expansionSpeakers?.model ?? area.speakers?.model;
    const promotedSpkrEquip = spkrEquipment[promotedSpkrModel];
    // Height must be re-resolved for the same reason as coverage, and with the same consequence.
    // The spread below would otherwise inherit the BAR speaker's height, which is
    // `constants.area.talkerHeight` (constants.js `barspkr`: `height: () => talkerHeight`) — the bar
    // sits at talker level. Carried onto a ceiling/pendant speaker that makes
    // `speakerHeight - talkerHeight` exactly 0, so the coverage radius
    // (`roomCalculations.ts`: `(speakerHeight - talkerHeight) * tan(coverageAngle)`) is 0,
    // `getSpeakerAxisCount`'s `!(spacing > 0)` guard returns 1 on both axes, and the entire room
    // collapses to ONE centred speaker with no usable SPL plot.
    //
    // Resolution order mirrors the expansion placement path exactly (`roomCalculations.ts`
    // `expSpkrHeight`, PIG-612): a stored expansion height wins — that is the user-configured hang
    // height for a pendant, which must survive the promotion — otherwise the model's own default
    // (`ceilingspkr`/`pendantspkr`), so the promoted group lands where a fresh selection would.
    // Guarded on real geometry because those `height()` helpers read `area.ceilingHeight.min`
    // unguarded, and this reducer must stay safe on partially-built areas.
    const promotedSpkrHeight = area.expansionSpeakers?.height
      ?? (typeof promotedSpkrEquip?.height === 'function' && typeof area.ceilingHeight?.min === 'number'
        ? promotedSpkrEquip.height(area)
        : (area.ceilingHeight?.min ?? area.speakers?.height));
    area.speakers = {
      ...area.speakers,
      model: promotedSpkrModel,
      modelName: area.expansionSpeakers?.modelName ?? area.speakers?.modelName,
      mountingType: area.expansionSpeakerType === 'pendant' ? 'pendant' : 'ceiling',
      height: promotedSpkrHeight,
      coverage: spkrCoverage,
      layout: spkrLayout,
      qty: spkrLayout.length,
      // The bar's SPL plot was computed for the bar's own dispersion at talker height — meaningless
      // for a ceiling/pendant grid, and it would otherwise render until HearSpace next recomputes.
      plotData: {},
    };
    area.customized.speakers = false;
  } else if (spkrEquipment[area.speakers?.model]?.isBarSpkr) {
    // No expansion speakers to promote, but the primary speaker IS the bar's own driver — a
    // physical part of the bar that just got removed. Left alone, the design keeps
    // `model: 'barspkr'` (`modelName: null`, height = talkerHeight, all-false coverage, layout
    // pinned to the bar's wall position) on a room that no longer has a bar. That is not cosmetic:
    //   - `calculateEquipmentTable` resolves the speaker name through `layout[0].modelName` (the
    //     stale BAR name), which defeats its own 'Desono C-IC6' fallback, finds no catalog match,
    //     and emits NO speaker/cable/amp rows at all — a quote with zero speakers, console.warn only.
    //   - `createDeviceAdjutmentAndAim` / `MuiDevices` key off `isBarSpkr` alone (no `hasBar`
    //     check), so the canvas keeps drawing a wall-hugging, 90°-rotated video-bar icon.
    //   - `actions.js` disables Remove for bar speakers, so the user cannot delete the orphan.
    //
    // `resetArea`'s speaker migration (logic.js, case 'microphones') is written for exactly this
    // (`(!isBarMic && wasBarMic) ? 'ceilingspkr' : speakerModel`) and its `: isBarSpkr` fallback
    // even exists because "'barspkr' is only ever valid alongside a bar mic". It cannot fire here
    // though: it reads `_oldModel`, which this function has already rewritten to the promoted mic,
    // so `wasBarMic` is false and the safety net is bypassed rather than triggered.
    //
    // 'ceilingspkr' is the same explicit migration target both that reducer and `setMicrophones`'
    // wall-speaker migration use — pinned by key (not `Object.keys().find()`) so a future catalog
    // reordering can't silently change the default. Coverage/height are computed here rather than
    // via the equipment `coverage()`/`height()` helpers because those read `area.table.shape` /
    // `area.ceilingHeight.min` unguarded, and this function must stay safe on partially-built areas
    // (same defensive stance as the geometry guards in `attemptCeilingHeightMitigation`).
    const ceilingSpkr = spkrEquipment.ceilingspkr;
    const spkrHasTable = Boolean(area.table?.shape);
    area.speakers = {
      ...area.speakers,
      model: 'ceilingspkr',
      modelName: ceilingSpkr?.modelName ?? area.speakers?.modelName,
      mountingType: 'ceiling',
      height: area.ceilingHeight?.min ?? area.speakers?.height,
      coverage: { focused: spkrHasTable, full_room: !spkrHasTable },
      // The bar speaker's coordinates describe the bar's wall position and its SPL plot was
      // computed for the bar's dispersion — both meaningless for a ceiling grid. Cleared here and
      // recomputed by the follow-up placement pass scheduled below, the same contract the ceiling
      // mic branch uses for its own empty layout.
      layout: [],
      qty: 0,
      plotData: {},
    };
    area.customized.speakers = false;
  }

  // Read by `calculateDevicesMiddleware` right after this dispatch settles, then cleared via
  // `clearPendingCeilingPromotionRecalc`. It asks for the real, DOM-based placement pass this
  // reducer cannot run synchronously (that pass re-dispatches `setMicrophones`, which wipes BOTH
  // layouts and re-places mics and speakers from scratch).
  //
  // ALWAYS set, because EVERY promotion invalidates EVERY layout it carries forward. This
  // function only ever runs behind `applyUcxCapacityFallback`'s `if (hadBar)` guard, so the design
  // being promoted always had a bar and always loses it — and every group in it was placed
  // *around* that bar:
  //   - the promoted PRIMARY mic. Ceiling commits an estimated qty with an empty layout (see the
  //     doc comment above) and obviously needs the pass. Table looks like it doesn't — it carries
  //     over real, placed coordinates — but those came from the reduced-arc expansion placement
  //     (spec §7 Steps 3-7), which deliberately covers only the arc of the table path the bar
  //     could NOT reach. As a standalone primary, TTM-X has to cover the WHOLE path, so the
  //     wholesale copy is a systematic UNDERCOUNT clustered at the far end of the table.
  //   - the promoted expansion SPEAKERS (the `hadExpansionSpeakers` branch above). Same shape:
  //     that layout was sized to fill the gap the bar's own drivers left, not to cover the room.
  //   - the migrated BAR speaker (the `isBarSpkr` branch above), which commits an empty layout
  //     outright.
  //
  // Gating on `wasCeiling || needsSpeakerRecalc` — as this did — left exactly one combination
  // with no follow-up pass at all: a TABLE promotion whose speakers came from EXPANSION rather
  // than from the bar, since the `hadExpansionSpeakers` branch set neither flag. That is the
  // common shape for the configuration that trips the §2.3 capacity ceiling in the first place
  // (bar + table expansion mics + expansion speakers), and it shipped both undercounts as final.
  state._pendingCeilingPromotionRecalc = true;

  area.expansionMicType = null;
  area.expansionMicrophones = null;
  area.expansionMicCoverage = null;
  area.expansionMicManualOptIn = false;
  area.expansionMicSuppressed = false;
  area.expansionSpeakerType = null;
  area.expansionSpeakers = null;
  area.expansionSpeakerCoverage = null;
  area.expansionSpeakerRecommended = false;
  // Symmetric with `expansionSpeakerRecommended` above, and load-bearing for the same reason: this
  // promotion removes the BAR (`hasBar = false` below, primary migrated to tcm_x/ttm_x), and both
  // recommendation flags describe a gap in a *bar's* coverage. Leaving the mic flag set strands a
  // claim about a device the design no longer has.
  //
  // Concretely, without this the capacity fallback re-opens the very dialog it just ruled out.
  // `calculateDevicesMiddleware`'s view-switch handler opens `expansionMicRecommendation` on
  // `expansionMicRecommended && expansionMicType === null` — and this function sets the second
  // half of that predicate. Nothing recomputes the first half in between: neither
  // `container/setPlatform` nor `container/setProductFamily` is a `BAR_GAP_TRIGGERS` entry, so a
  // fallen-back design that flipped to Windows and was then put back on Android by hand reaches
  // the microphones menu with a stale `true` and prompts "Expansion Microphones recommended" —
  // offering the exact bar + expansion configuration that just failed the §2.3 capacity check,
  // and bypassing the `_ucxCapacityFallenBack` guard that exists to enforce §2.6's
  // "degrades only, never restores Devio UCX/the bar".
  //
  // Note the flag is not merely stale but contradicted: `calculateBarCoverageGap` returns false
  // on its first guard once the primary is no longer a bar, so a re-evaluation would clear it.
  // This reset is what makes the persisted flag agree with that.
  area.expansionMicRecommended = false;
  area.hasBar = false;
};

/**
 * Devio UCX capacity fallback (spec §2.2/§2.3) — fires when `isUCXViable`
 * says the design's CURRENT mic/speaker count no longer fits Devio UCX.
 * Branches on `androidExplicitlySelected` (spec §2.1):
 *   - true  → keep Android, route through Tesira + UCC-UCX1000-T (USB). This
 *     reuses the existing, already-shipped Tesira-via-USB mechanism (same
 *     `productFamily: 'Tesira'` + platform-stays-'Android' shape the old
 *     port-overflow branch below produces) — logic.js's
 *     `isTreeFrogAndroidUC` gate doesn't care which trigger set it.
 *   - false → drop Android/UCX entirely. Devio SCX valid ≤8 mics (checked
 *     AFTER any bar removal above), else Tesira. This branch has no existing
 *     precedent in the codebase — every prior fallback here targets Tesira
 *     only, never Devio SCX.
 *
 * Sets `_ucxCapacityFallenBack` so the design never gets silently promoted
 * back to Devio/the bar by an unrelated later recalculation (spec §2.6:
 * "degrades only... never restores Devio UCX/the bar") — see its read site
 * in the `eligible` tail below (PIG-651's auto-restore is for the port-only
 * trigger, not this one).
 */
const applyUcxCapacityFallback = (state: any, area: any) => {
  const hadBar = area.expansionMicType === 'ceiling' || area.expansionMicType === 'table';
  if (hadBar) promoteExpansionToPrimary(state, area);

  if (area.androidExplicitlySelected) {
    area.productFamily = 'Tesira';
  } else {
    area.platform = 'Windows';
    delete area._preAndroidProductFamily;
    // `qty` first, not `layout.length` — a ceiling promotion (`promoteExpansionToPrimary`) commits
    // an ESTIMATED qty with an intentionally EMPTY layout (real positions need a DOM-based
    // recalculation pass this reducer can't run synchronously — see that function's doc comment),
    // so `layout.length` would read 0 here and always misread the design as within Devio SCX's
    // capacity regardless of the real count. The table branch (wholesale layout copy) still has
    // `qty === layout.length`, so preferring `qty` doesn't change that case's answer.
    const micCount = area.microphones?.qty ?? area.microphones?.layout?.length ?? 0;
    area.productFamily = micCount <= 8 ? 'Devio' : 'Tesira';
  }
  area._ucxCapacityFallenBack = true;
  area.ucxMicTypeSwitchAllowed = false;

  applyDevioMicModelName(area);
  state._toastMessage = hadBar
    ? 'This room requires more microphones than Devio UCX can support. Bar has been removed.'
    : 'This room requires more microphone coverage than Devio UCX can provide. Updating system.';
  state._toastSeverity = 'warning';
};

/**
 * Dry-run preview of what ceiling EXPANSION mic layout `calcExpansionCeilingMicPlacement` would
 * place for the CURRENT area (room, table, acoustics, bar model) — without writing anything to
 * state. Mirrors, input-for-input, the real call `computeExpansionCeilingMicCoords`
 * (reduxModules/calculations/roomCalculations.ts) makes on an actual `setExpansionMicType`
 * switch, minus the tile-snapping/customized-layout-freeze wrapper around it. Reuses the exact
 * same placement function and coverage-box math the real path uses (`computeCoverageBoxesForArea`,
 * extracted from `getCoverageBoxes` for this purpose) rather than re-deriving either.
 *
 * Returns real, renderable device objects (`{ model: 'dcm_x', modelName, location: {x, y, z} }`)
 * — the same shape `computeExpansionCeilingMicCoords` commits, just without its tile-snapping
 * pass — NOT placeholder/blank entries. `MuiDevices` maps every device in a layout and reads
 * fields (e.g. `.rotation`) off each one; an array of literal `undefined` (e.g. from
 * `Array.from({ length: n })`) crashes it the moment anything renders that layout before a real
 * placement pass overwrites it. Callers that only need the count (`dryRunCeilingExpansionMicCount`
 * below) still get one, cheaply, off this same result — no duplicated computation.
 *
 * There is no equivalent dry-run for the TABLE candidate: `calcExpansionTableMicPlacement`
 * samples a real, rendered SVG path element in canvas-pixel space (`path.getPointAtLength`,
 * `path.getCTM`) — it has no meters-only/DOM-free mode, and reimplementing that sampling here
 * would itself be the kind of duplicated logic this dry-run is meant to avoid. See
 * `computeMicTypeSwitchFlag` below for how that asymmetry is handled.
 *
 * @param heightOverride - when given, predicts the layout at THIS height instead of the area's
 *   currently-configured expansion mic height. Used by `attemptCeilingHeightMitigation` (Case 3a,
 *   spec §2.2/§2.3 fallback) to answer "how many mics would this take at the acoustics-optimal
 *   height" without duplicating this function for that one differing input.
 */
const dryRunCeilingExpansionMicLayout = (state: any, area: any, heightOverride?: number): { qty: number; coords: any[] } => {
  // Defensive fallback for an area missing the geometry a real dry-run needs (dimensions/
  // location/table — always present on a real design, but not guaranteed on a partially-built
  // one, e.g. legacy state mid-migration). Falls back to the current expansion mic layout,
  // matching what the old (pre-dry-run) approximation always used for the count.
  if (!area?.dimensions || !area?.location || !area?.table) {
    const fallbackLayout = area?.expansionMicrophones?.layout ?? [];
    return { qty: fallbackLayout.length || (area?.expansionMicrophones?.qty ?? 0), coords: fallbackLayout };
  }
  const constants = toolIndex[state.currentTool].constants;
  const coverageBoxes = computeCoverageBoxesForArea(area, constants);
  const isFocused = getExpansionCeilingIsFocused(area);
  const baseBox = coverageBoxes[isFocused ? 'focused' : 'full_room'];
  const acousticIndex = indexOf(constants.acoustics, area.acoustics);
  // Same height resolution setExpansionMicType/computeExpansionCeilingMicCoords use: the
  // expansion group's own configured height, falling back to ceiling height — unless the caller
  // asked us to predict a DIFFERENT height (Case 3a).
  const expansionMicHeight = heightOverride
    ?? area.expansionMicrophones?.height
    ?? area.ceilingHeight?.min
    ?? area.ceilingHeight
    ?? DEFAULT_CEILING_HEIGHT_M;

  const specResult: any = calcExpansionCeilingMicPlacement({
    room: { left: area.location?.left ?? 0 },
    baseBox,
    acousticIndex,
    isFocused,
    barModel: area.microphones?.model,
    ceilingHeight: expansionMicHeight,
  });

  const coords = (specResult?.coords ?? []).map((d: any) => ({ ...d, location: { ...d.location, z: expansionMicHeight } }));
  return { qty: specResult?.qty ?? 0, coords };
};

/** Count-only convenience wrapper — see `dryRunCeilingExpansionMicLayout` above. */
const dryRunCeilingExpansionMicCount = (state: any, area: any, heightOverride?: number): number =>
  dryRunCeilingExpansionMicLayout(state, area, heightOverride).qty;

/**
 * Devio UCX capacity fallback — height-lowering mitigation ("Case 3a" per the user's own
 * follow-up answer; not itself a numbered section of the capacity-fallback spec, which only
 * describes the mechanical "remove bar" outcome). Tried BEFORE `applyUcxCapacityFallback`
 * (Case 2) fires, on the reasoning that lowering the ceiling expansion mic to its
 * acoustics-optimal height (constants.js's canonical `dcm_x.height()` — see the height-formula
 * unification elsewhere in this file) increases each mic's coverage radius
 * (`calcExpansionCeilingMicPlacement`'s `side(maxPickupDist, height - talkerHeight)`: a lower
 * mic means a smaller vertical drop and thus a LARGER horizontal radius), which can reduce the
 * mic count the placement algorithm actually requires — sometimes enough to drop back into (or
 * to a more permissive row of) the §2.3 capacity table, avoiding the bar removal entirely.
 *
 * Only applicable when:
 *   - the design's expansion is CEILING — table expansion mics mount at a fixed tabletop height,
 *     so there is no height lever to pull (same asymmetry `dryRunCeilingExpansionMicCount` above
 *     documents for the table candidate)
 *   - the layout isn't customized (`customized.expansionMicrophones` / `expansionMicManualOptIn`)
 *     — a hand-placed layout is real user work `calculateDevicesMiddleware.ts`'s placement pass
 *     deliberately preserves rather than overwrites, so lowering height here wouldn't actually
 *     change the committed mic count and this mitigation would silently accomplish nothing
 *   - the acoustics-optimal height is strictly BELOW the current height — otherwise the design
 *     is already at its best achievable count and there's nothing to gain by "lowering" further
 *
 * Only PREDICTS the outcome via `dryRunCeilingExpansionMicCount` (reusing the exact placement
 * math Case 3b's switch flag already relies on, per the user's "no duplicate code/logic"
 * instruction) rather than committing a new mic layout itself. When the prediction says the
 * lowered height would be viable, this commits only the height field (plus the toast) —
 * `calculateDevicesMiddleware.ts`'s always-run ceiling expansion placement pass picks up the new
 * height on this SAME dispatched action and re-places the mics for real, exactly as it already
 * does for any other ceiling expansion height change (acoustics edits, manual height edits,
 * etc.). No placement logic is duplicated here — only the decision of whether to attempt the
 * mitigation, and the resulting toast.
 *
 * @param assumeApplicable - forwarded to `isUCXViable` unchanged (see its doc comment) — true
 *   when the caller (`revalidateUcxEligibility`) already knows this design is exclusive or about
 *   to become exclusive on this same dispatch, so the predicted-area check below must evaluate
 *   the REAL predicted count instead of self-disabling because `platform`/`productFamily` haven't
 *   been flipped to Android/Devio yet.
 * @returns true when the mitigation was applied (height lowered, toast set) — caller should
 *   skip `applyUcxCapacityFallback`. false when no mitigation was possible or it wouldn't have
 *   resolved the overflow — caller proceeds to Case 2 as before.
 */
const attemptCeilingHeightMitigation = (state: any, area: any, assumeApplicable: boolean): boolean => {
  if (area.expansionMicType !== 'ceiling') return false;
  if (area.customized?.expansionMicrophones || area.expansionMicManualOptIn) return false;
  // Defensive guard for an area missing geometry either `dcmConfig.height()` (ceilingHeight.min)
  // or the dry-run placement below (dimensions/location/table) needs — always present on a real
  // design, but not guaranteed on a partially-built one (e.g. a test fixture or legacy state
  // mid-migration). No mitigation is attempted rather than throwing; the caller falls through to
  // Case 2 as before.
  if (!area?.dimensions || !area?.location || !area?.table || typeof area?.ceilingHeight?.min !== 'number') {
    return false;
  }

  const constants = toolIndex[state.currentTool].constants;
  const dcmConfig = constants.equipment.microphones.dcm_x;
  if (typeof dcmConfig?.height !== 'function') return false;

  const optimalHeight = dcmConfig.height(area);
  const currentHeight = area.expansionMicrophones?.height;
  if (typeof currentHeight !== 'number' || !(optimalHeight < currentHeight - 1e-6)) return false;

  const predictedCount = dryRunCeilingExpansionMicCount(state, area, optimalHeight);
  const predictedArea = {
    ...area,
    expansionMicrophones: {
      ...area.expansionMicrophones,
      qty: predictedCount,
      layout: Array.from({ length: predictedCount }),
    },
  };
  if (!isUCXViable('ceiling', predictedArea, { assumeApplicable }).viable) return false;

  area.expansionMicrophones.height = optimalHeight;
  area.expansionMicrophones.maxHeight = optimalHeight;
  state._toastMessage = 'Ceiling expansion microphone height lowered to keep this room within Devio UCX\'s microphone capacity.';
  state._toastSeverity = 'warning';
  return true;
};

/**
 * Devio UCX capacity fallback — Case 3b ("if switching to expansion ceiling, show the toast" —
 * the user's own follow-up answer; also not a numbered spec section). The table-active
 * counterpart to Case 3a above: when a bar + TABLE expansion design is over §2.3 capacity,
 * try auto-switching to CEILING expansion before removing the bar (Case 2) — ceiling's capacity
 * rows are never stricter than table's for the same mic-count band (§2.3: ceiling 12/8 vs table
 * 8/0), so a table design that overflows can sometimes fit as ceiling instead.
 *
 * Reuses the exact same real dry-run `computeMicTypeSwitchFlag` (see its doc comment — this is
 * an implementation safeguard, not a spec-numbered rule) already relies on for the
 * ceiling candidate — `dryRunCeilingExpansionMicCount` — rather than re-deriving the placement
 * math, and reuses `isUCXViable`'s existing "read expansion mic/speaker counts whenever
 * `hasExpansion` is true" behavior (true for both 'ceiling' and 'table') to check the predicted
 * count without needing to mutate `expansionMicType` on the predicted area first.
 *
 * Unlike Case 3a, this is NOT gated on `customized`/`expansionMicManualOptIn` — like Case 2's
 * bar removal, an actual over-capacity table design is a hard constraint the user's prior
 * customization can't make viable again, so this fires regardless (mirroring
 * `applyUcxCapacityFallback`'s own unconditional directness for the same reason).
 *
 * Commits only the ceiling group's INTENT (model/height, empty layout) — the same shape
 * `setExpansionMicType('ceiling')` itself sets up for a fresh selection — and lets
 * `calculateDevicesMiddleware.ts`'s always-run ceiling placement pass compute the real layout
 * for this same dispatched action, exactly as a manual switch already does. No placement logic
 * is duplicated here.
 *
 * @param assumeApplicable - forwarded to `isUCXViable` unchanged — see
 *   `attemptCeilingHeightMitigation`'s matching param doc above.
 * @returns true when the switch was applied (toast set) — caller should skip
 *   `applyUcxCapacityFallback`. false when not applicable (not a table-active design) or the
 *   switch wouldn't have resolved the overflow either — caller proceeds to Case 2.
 */
const attemptTableToCeilingSwitch = (state: any, area: any, assumeApplicable: boolean): boolean => {
  if (area.expansionMicType !== 'table') return false;
  // Defensive guard, same reasoning as `attemptCeilingHeightMitigation`: without real room
  // geometry, `dryRunCeilingExpansionMicCount` can only fall back to reusing the CURRENT
  // (table-shaped) count as a low-confidence stand-in for a ceiling placement, which isn't a
  // sound basis for auto-committing a full type switch. No switch is attempted rather than
  // acting on that guess; the caller falls through to Case 2 as before.
  if (!area?.dimensions || !area?.location || !area?.table || typeof area?.ceilingHeight?.min !== 'number') {
    return false;
  }

  const constants = toolIndex[state.currentTool].constants;
  const dcmConfig = constants.equipment.microphones.dcm_x;
  if (typeof dcmConfig?.height !== 'function') return false;

  const newCeilingHeight = dcmConfig.height(area);
  // The full dry-run LAYOUT, not just its count — the coords are committed below, so predicting
  // them once here (rather than calling the count-only wrapper and re-deriving the placement
  // later) keeps the capacity decision and the layout it authorizes strictly in sync.
  const predicted = dryRunCeilingExpansionMicLayout(state, area, newCeilingHeight);
  const predictedArea = {
    ...area,
    expansionMicrophones: {
      ...area.expansionMicrophones,
      qty: predicted.qty,
      layout: predicted.coords,
    },
  };
  if (!isUCXViable('ceiling', predictedArea, { assumeApplicable }).viable) return false;

  const placeholders = constants.expansionMicPlaceholders;
  const hasTable = Boolean(area.table?.shape);
  area.expansionMicType = 'ceiling';
  area.expansionMicCoverage = { focused: hasTable, full_room: !hasTable };
  // Seeded with the real, renderable dry-run coords — NOT an empty `qty: 0, layout: []`
  // placeholder — exactly as `setExpansionMicType`'s own ceiling branch does (see the long
  // comment there). This used to commit an empty layout on the reasoning that
  // `calculateDevicesMiddleware`'s ceiling placement pass would fill it in "on this same
  // dispatched action, exactly as a manual switch already does". That holds only when the
  // dispatch that triggered this switch is itself a recalculation trigger. It usually isn't:
  // the common path here is the middleware's OWN `setExpansionMicLayout` (or
  // `setExpansionSpeakerLayout`) commit landing a fresh, over-capacity TABLE layout after an
  // acoustics change — and both of those are in the middleware's SKIP_RECALCULATION list, so no
  // further pass ever ran and the design was left on Ceiling with zero mics on the canvas.
  area.expansionMicrophones = {
    model: 'dcm_x',
    modelName: placeholders.ceiling,
    qty: predicted.qty,
    layout: predicted.coords,
    height: newCeilingHeight,
  };
  area.expansionMicManualOptIn = false;
  if (area.customized) area.customized.expansionMicrophones = false;

  // Belt-and-braces for the same problem: the seed above is `calcExpansionCeilingMicPlacement`'s
  // grid placement, which is the real answer for grid-shaped tables but only an approximation for
  // the path-based shapes (round/keystone/vshape) `computeExpansionCeilingMicCoords` routes
  // through the `#micPath` shared core, and it skips the tile-snapping pass either way. This flag
  // asks `calculateDevicesMiddleware` for the real, DOM-based placement pass a manual
  // Table→Ceiling switch gets — same transient-signal convention and pickup site as
  // `_pendingCeilingPromotionRecalc` (see `promoteExpansionToPrimary`). Re-entry is impossible:
  // the follow-up dispatch runs against `expansionMicType === 'ceiling'`, which this function's
  // own first guard rejects.
  state._pendingExpansionCeilingRecalc = true;

  state._toastMessage = 'The coverage of the tabletop expansion microphones is not adequate. Switched to ceiling expansion microphones.';
  state._toastSeverity = 'warning';
  return true;
};

/**
 * Mic-type-switch viability flag — whether the ceiling↔tabletop EXPANSION toggle is
 * currently allowed. NOTE: unlike the §2.2/§2.3 capacity fallback this flag feeds into, this
 * gating behavior is NOT itself spec-mandated — no written spec (the Documentation/specs
 * mirrors or the source Confluence algorithm docs) calls for hiding the non-viable candidate
 * type from the selector. It's an implementation safeguard added on top of the real capacity
 * math (`isUCXViable`) to keep the user from picking a type that would immediately overflow —
 * kept because it's still useful UX, but comments elsewhere citing this as "spec §2.5" were
 * wrong and have been corrected to point back here instead.
 *
 * Scoped to "Android": a viable Devio UCX design, or a fallen-back design with
 * `androidExplicitlySelected === true` (Tesira-alongside, UCX1000 retained).
 * Once `androidExplicitlySelected === false` (fully off Android), switching
 * is unrestricted — this flag simply isn't consulted by the UI in that case.
 *
 * Ceiling candidate: a real dry-run (`dryRunCeilingExpansionMicCount`) recomputes the count
 * the placement algorithm would actually produce for ceiling, and that count — not the
 * current, possibly-table-shaped count — is what gets checked against the capacity table.
 *
 * The dry-run is run at the acoustics-OPTIMAL ceiling mic height (`dcm_x.height(area)`), not
 * whatever height happens to already be on `area.expansionMicrophones` (meaningless here — a
 * table-active design has no ceiling expansion height set at all, so that field is undefined
 * and the dry-run would otherwise silently fall back to the room's raw ceiling height instead).
 * `attemptTableToCeilingSwitch` (Case 3b, above) already passes this same override for exactly
 * this reason; without it here too, this flag could disagree with Case 3b's own math — a real
 * switch-to-ceiling calculated (and committed) at the optimal height, while this flag evaluated
 * the raw, un-lowered ceiling height, which needs MORE mics (a higher mount means a smaller
 * coverage radius) and can push a genuinely-viable switch over the hard mic-count cutoff,
 * wrongly hiding Ceiling from the selector for a design that would actually fit fine.
 *
 * Table candidate: still the cheap approximation (the CURRENT mic count, evaluated against
 * table's capacity row) — `calcExpansionTableMicPlacement` needs a live rendered SVG path, so
 * an accurate dry-run isn't reachable from here (see the doc comment on
 * `dryRunCeilingExpansionMicCount`). Worth revisiting if a DOM-free table placement estimate is
 * ever built.
 */
const computeMicTypeSwitchFlag = (state: any, area: any): boolean => {
  if (area.platform !== 'Android') return false;
  if (area.productFamily !== 'Devio' && !area.androidExplicitlySelected) return false;
  const hasExpansion = area.expansionMicType === 'ceiling' || area.expansionMicType === 'table';
  if (!hasExpansion) return false;
  const candidateType = area.expansionMicType === 'ceiling' ? 'table' : 'ceiling';

  if (candidateType === 'ceiling') {
    const constants = toolIndex[state.currentTool].constants;
    const dcmConfig = constants.equipment.microphones.dcm_x;
    const optimalHeight = typeof dcmConfig?.height === 'function' ? dcmConfig.height(area) : undefined;
    const dryRunCount = dryRunCeilingExpansionMicCount(state, area, optimalHeight);
    const dryRunArea = {
      ...area,
      expansionMicrophones: { ...area.expansionMicrophones, qty: dryRunCount, layout: Array.from({ length: dryRunCount }) },
    };
    return isUCXViable(candidateType, dryRunArea).viable;
  }

  return isUCXViable(candidateType, area).viable;
};

/**
 * Sets a toast, falling back to a second independent slot (`_toastMessage2` etc.) when the
 * primary slot is already occupied — rather than clobbering it. Needed because
 * `revalidateUcxEligibility` (called at the end of `setExpansionMicType` / `setExpansionMicrophones`)
 * can set its own toast (e.g. "Platform switched to Android...") on the SAME dispatch as an
 * acoustics-limited-height toast set earlier in the reducer body; both are independently true and
 * should both reach the user, not have one silently overwrite the other. `toastMiddleware.ts`
 * shows both slots, and `utilities/toastManager.ts`'s global queue displays them one after another
 * rather than dropping either. Only two slots are supported today — there is no known case that
 * needs a third toast in one dispatch; if one arises, generalize this to a real array queue.
 */
const setOrQueueToast = (state: any, message: string, severity: string, heightMeters?: number) => {
  if (state._toastMessage) {
    state._toastMessage2 = message;
    state._toastSeverity2 = severity;
    if (heightMeters !== undefined) state._toastHeightMeters2 = heightMeters;
  } else {
    state._toastMessage = message;
    state._toastSeverity = severity;
    if (heightMeters !== undefined) state._toastHeightMeters = heightMeters;
  }
};

/**
 * Applies the Devio UCX UC Compute rule and queues the explanatory toast when it
 * actually changed something. 'Zoom Rooms' has no Devio UCX compute SKU, so a
 * design that resolves to Devio UCX (Devio family + Android) falls back to
 * Microsoft Teams — see normalizeUcComputeForUcx (utilities/treeFrogBundleMatching.js)
 * and the matching UC Compute list filter in
 * toolConstants/conferenceroomDesigner/menus.js. Uses `setOrQueueToast` because
 * the same dispatch may already have set a platform/capacity toast that is
 * independently true.
 */
const applyUcxUcComputeRule = (state: any, area: any) => {
  if (normalizeUcComputeForUcx(area)) {
    setOrQueueToast(
      state,
      `Zoom Rooms is not available on Devio UCX — UC Compute set to ${UCX_UC_COMPUTE_FALLBACK}.`,
      'warning'
    );
  }
};

/**
 * Android port-overflow fallback (§5.4): Platform stays Android, Family → Tesira
 * (DSP + UCC-UCX1000-T over USB). Toasts only on the transition into the
 * fallback. Used for a non-bar design whose speakers exceed the Devio UCX port
 * capacity; mirrors the `port-overflow` branch of `revalidateUcxEligibility`.
 */
const applyPortOverflowFallback = (state: any, area: any) => {
  const enteringFallback = area.productFamily !== 'Tesira';
  area.productFamily = 'Tesira';
  if (enteringFallback) {
    state._toastMessage = PORT_OVERFLOW_FALLBACK_TOAST;
    state._toastSeverity = 'warning';
  }
  applyDevioMicModelName(area);
  area.ucxMicTypeSwitchAllowed = computeMicTypeSwitchFlag(state, area);
};

/** Placed device count: layout length when populated, otherwise `qty`. */
const placedDeviceCount = (device: any): number => {
  const placed = device?.layout?.length ?? 0;
  return placed > 0 ? placed : (device?.qty ?? 0);
};

export const revalidateUcxEligibility = (state: any) => {
  const area = state.areas[state.activeTab];
  if (!area) return;
  const eligibility = isDevioUcxEligible(area);

  // BMA360D (ceiling-tile mic) is Dante-based — it never rides Devio UCX, and
  // there's no Devio SCX support for it either, so it ships on Tesira only.
  // Platform is pinned to Windows and Product Family to Tesira unconditionally,
  // regardless of what the design's platform/family were before this mic was
  // selected (a stale `_preAndroidProductFamily` snapshot from an earlier bar/
  // expansion design could otherwise leave Family on Devio after switching to
  // BMA360D). See RadioButtons.tsx / Selector.tsx for the matching "visible but
  // disabled" UI lock.
  const isCeilingTileMic = Boolean(area.microphones?.model) && MIC_CATEGORIES.CEILING_TILE.has(area.microphones.model);
  if (isCeilingTileMic) {
    if (area.platform !== 'Windows') {
      area.platform = 'Windows';
      state._toastMessage = eligibility.reason ?? 'Devio UCX is not compatible with this configuration.';
      state._toastSeverity = 'warning';
    }
    delete area._preAndroidProductFamily;
    area.productFamily = 'Tesira';
    applyDevioMicModelName(area);
    return;
  }

  // Recomputed directly here (not read from `eligibility.exclusive`, which is `false` whenever
  // the older port-chain `isPortOverflow` fires below) so it's available BEFORE the capacity
  // check that follows — see that check's own comment for why the ordering matters.
  const hasExpansionMics = area.expansionMicType === 'ceiling' || area.expansionMicType === 'table';
  const hasExpansionSpeakers = area.expansionSpeakerType === 'ceiling' || area.expansionSpeakerType === 'pendant';
  const isBarWithExpansion = Boolean(area.microphones?.model) && MIC_CATEGORIES.BAR.has(area.microphones.model)
    && (hasExpansionMics || hasExpansionSpeakers);
  const isExclusiveDesign = eligibility.exclusive || isBarWithExpansion;

  // Devio UCX capacity fallback (spec §2.2/§2.3) — checked BEFORE bar+expansion
  // exclusivity below, since a design over Devio UCX's mic/speaker capacity
  // must fall back regardless of what exclusivity would otherwise force (the
  // exclusive branch unconditionally forces platform=Android/family=Devio and
  // returns, which would otherwise never let an over-capacity bar+expansion
  // design reach a fallback unless it ALSO happened to trip the older,
  // port-chain-based `isPortOverflow` check below).
  //
  // `assumeApplicable: isExclusiveDesign` — `isUCXViableForArea` otherwise self-disables
  // (returns `applicable: false`) for a design that isn't ALREADY, really, Devio+Android. On the
  // very FIRST dispatch that makes a design exclusive (e.g. selecting a bar mic in a room whose
  // real ceiling-expansion mic count is already over capacity), `platform`/`productFamily` haven't
  // been flipped to Android/Devio yet — so without this, the check would no-op here, fall through
  // to the exclusivity branch below, and commit platform=Android + its toast for a design that's
  // already known (from its real, already-placed expansion mics/speakers) to be over capacity.
  // The user would then see "Platform switched to Android" (and any height-adjustment toast from
  // the same dispatch) immediately followed by a capacity-fallback toast on a LATER recompute —
  // two contradictory-feeling toasts for a shape that was never actually viable. Passing this
  // through makes the hypothetical-Devio+Android normalization `isPortOverflow` already does (see
  // `isUCXViable`'s doc comment) apply here too, so the real mic/speaker counts are checked before
  // ANY of that commits.
  const ucxViability = isUCXViableForArea(area, { assumeApplicable: isExclusiveDesign });
  if (ucxViability.applicable && !ucxViability.viable) {
    // Non-bar (ceiling / table primary) design over Devio UCX capacity, routed
    // like the port-overflow branch below (§5.4: Android stays, Family → Tesira +
    // UCC-UCX1000-T, port-budget toast) instead of the §2.2 capacity fallback:
    //   - speakers over what the ports can amplify (≤ 4 mics) — e.g. 3 ceiling
    //     mics switched to wall speakers; or
    //   - more than 4 mics on a design the user put on Android themselves
    //     (`androidExplicitlySelected`) — the §2.2 true branch lands on the same
    //     Android + Tesira shape anyway.
    // Unlike the §2.2 fallback this is NOT sticky (`_ucxCapacityFallenBack` stays
    // unset), so the eligible tail promotes the design back to Devio once it fits
    // again — e.g. Undo after adding mics, or switching back to C-IC6. A > 4-mic
    // design that was NOT explicitly put on Android keeps §2.2 Case 1 (→ Windows,
    // Devio SCX ≤ 8 mics, else Tesira).
    if (!hasExpansionMics && (ucxViability.micCount <= 4 || area.androidExplicitlySelected)) {
      applyPortOverflowFallback(state, area);
      return;
    }
    // Case 3a (ceiling-active: lower height) / Case 3b (table-active: switch to ceiling) — both
    // mutually exclusive by their own `expansionMicType` guard, so trying both costs nothing.
    // Only falls back to removing the bar (Case 2) when neither mitigation applies or resolves it.
    if (!attemptCeilingHeightMitigation(state, area, isExclusiveDesign) && !attemptTableToCeilingSwitch(state, area, isExclusiveDesign)) {
      applyUcxCapacityFallback(state, area);
    }
    return;
  }

  // The §2.3 coverage table can be MORE permissive than the older port-chain
  // math (`isPortOverflow`, still used by `isDevioUcxEligible` below) for
  // some mic/speaker combinations — e.g. 4 ceiling expansion mics + 6 pendant
  // speakers fits §2.3's Ceiling/3-4-mics/max-8 row, but the DCM-XA swap's
  // port-chain capacity for that same combination is only 4. Once
  // `ucxViability` has confirmed a bar + expansion design is fine, it must
  // NOT fall through into the older `eligibility.fallback === 'port-overflow'`
  // branch below on the strength of the stale port math disagreeing — the
  // coverage table is authoritative for this decision now.

  // Bar + expansion mics/speakers make Devio UCX the only valid platform,
  // regardless of UC Compute — Family is always 'Devio' for this shape.
  // Auto-switch to Android from Windows (or unset) and keep it there —
  // `setPlatform` blocks a manual switch back to Windows while this holds.
  // Also re-assert productFamily='Devio' even when platform is ALREADY
  // Android (e.g. expansion mics/speakers were just added to a single-bar
  // Android design that had resolved to 'Parlé' with UC Compute = 'None' —
  // exclusivity always means Devio UCX, never Parlé).
  if (isExclusiveDesign) {
    if (area.platform !== 'Android') {
      area._preAndroidProductFamily = area.productFamily;
      area.platform = 'Android';
      state._toastMessage = 'Platform switched to Android — this bar design with expansion mics/speakers requires Devio UCX.';
      state._toastSeverity = 'warning';
    }
    area.productFamily = 'Devio';
    applyDevioMicModelName(area);
    // Now unambiguously Devio UCX — Zoom Rooms isn't offered here.
    applyUcxUcComputeRule(state, area);
    // A viable bar + expansion design always lands here (exclusive
    // is true whenever isBar && hasExpansion, per isDevioUcxEligible) —
    // compute the switch flag here too, not just in the non-exclusive
    // "eligible" tail below, which a bar + expansion design never reaches.
    area.ucxMicTypeSwitchAllowed = computeMicTypeSwitchFlag(state, area);
    return;
  }

  if (area.platform !== 'Android') {
    applyDevioMicModelName(area);
    // A single bar mic (CBC-2500, ABC-2500, VBC-2500a, VBC-2800 — no
    // expansion) on Windows always resolves Family to 'Parlé', regardless of
    // UC Compute. Non-bar designs are untouched (resolveBarProductFamily
    // returns null). See utilities/parleBarBundle.js.
    const barFamily = resolveBarProductFamily(area);
    if (barFamily) area.productFamily = barFamily;
    return;
  }

  if (!eligibility.eligible) {
    if (eligibility.fallback === 'port-overflow' || eligibility.fallback === 'speaker-incompatible') {
      // 'speaker-incompatible' (non-bar design whose primary speaker isn't the
      // bundle C-IC6 — wall, pendant, C-IC6LP, C-IC6LP-TAA) is solved exactly
      // like the port-overflow case below: the Devio UCX bundles only carry the
      // standard C-IC6, so the design goes Tesira à la carte + UCC-UCX1000-T.
      //
      // Per §5.4 "Android selected — system exceeds Tree Frog port limits"
      // Platform stays Android, the existing DSP calc runs
      // (bundle-first then individual DSP), and UCC-UCX1000-T is added as the
      // compute line item alongside it (both already fall out of `platform`
      // staying 'Android' — `isTreeFrogAndroidUC`/`familyLocked` gate on that,
      // not on productFamily). Family is auto-assigned to Tesira — Devio SCX
      // is invalid here (no USB port to reach UCX 1000), so the selector
      // stays locked to the single valid value, same as the within-budget
      // case.
      //
      // In practice this only fires for a non-bar ceiling/table PRIMARY
      // mic-count overflow — Tesira has NO equivalent for a bar + expansion
      // overflow (TC-05), so `addExpansionMic` / `addExpansionSpeaker` /
      // `setExpansionMicLayout` / `setExpansionSpeakerLayout` above cap
      // counts via `maxExpansionMicCountFor` / `maxExpansionSpeakerCountFor`
      // (treeFrogPorts.js) so a bar + expansion design can never reach
      // `isPortOverflow` through normal use. This branch stays uniform
      // (doesn't special-case bar shape) as a simple, safe fallback for any
      // bar + expansion design that reaches this state anyway (e.g. a legacy
      // saved design from before those caps existed).
      // Toast only on the transition into the fallback — this branch is
      // re-entered on every revalidation (e.g. each speaker change) while the
      // design stays fallen back, and repeating the same toast is noise.
      const enteringFallback = area.productFamily !== 'Tesira';
      area.productFamily = 'Tesira';
      if (enteringFallback) {
        state._toastMessage = eligibility.fallback === 'speaker-incompatible'
          ? SPEAKER_FALLBACK_TOAST
          : PORT_OVERFLOW_FALLBACK_TOAST;
        state._toastSeverity = 'warning';
      }
    } else {
      area.platform = 'Windows';
      if (area._preAndroidProductFamily !== undefined) {
        area.productFamily = area._preAndroidProductFamily;
        delete area._preAndroidProductFamily;
      }
      // The restored snapshot can be a bar-era 'Parlé' (e.g. a bar + expansion
      // design — resolved to 'Devio' while exclusive/Android — whose primary
      // mic is then switched to a hard-incompatible, non-bar shape like
      // Pendant). This branch is only reached for a non-bar mic (ceiling-tile
      // is handled earlier, unconditionally, above), so a restored 'Parlé' is
      // stale and meaningless here — same rule resolveBarProductFamily's own
      // "not a bar mic" clause already applies. Without this, the Product
      // Family control was left locked (disabled) on 'Parlé' instead of
      // resolving to the real Tesira/Devio choice ('Tesira' by default,
      // matching the ceiling-tile branch's unconditional Tesira pin).
      const clearedFamily = resolveBarProductFamily(area);
      if (clearedFamily) area.productFamily = clearedFamily;
      state._toastMessage = eligibility.reason ?? 'Devio UCX is not compatible with this configuration.';
    }
    applyDevioMicModelName(area);
    // The port-overflow sub-branch above lands in the same
    // Android+Tesira shape `applyUcxCapacityFallback`'s true branch produces
    // (this OLDER, port-chain-based trigger is a separate, still-valid path
    // to that same state — see the comment above it) — compute the switch
    // flag here too, not just in `applyUcxCapacityFallback` and the
    // non-exclusive "eligible" tail below. Safe for the Windows sub-branch
    // too: `computeMicTypeSwitchFlag` returns false immediately once
    // `platform !== 'Android'`.
    area.ucxMicTypeSwitchAllowed = computeMicTypeSwitchFlag(state, area);
    state._toastSeverity = 'warning';
    return;
  }
  applyDevioMicModelName(area);
  // Already on Android, eligible, non-exclusive: a single bar mic design
  // resolves 'Devio' vs 'Parlé' based on UC Compute (see
  // utilities/parleBarBundle.js).
  const barFamily = resolveBarProductFamily(area);
  if (barFamily) {
    area.productFamily = barFamily;
  } else if (area._ucxCapacityFallenBack) {
    // Spec §2.6: a design that fell back via the Devio UCX capacity fallback
    // (applyUcxCapacityFallback, above) degrades only — an unrelated later
    // recalculation reaching this "eligible" tail (e.g. this same design is
    // ALSO, coincidentally, within the older port-chain budget) must not
    // silently promote it back to Devio/the bar. Unlike PIG-651 below, which
    // intentionally restores a design that fell back ONLY via the port-only
    // trigger, once acoustics/room changes are the reason for improvement.
  } else {
    // Non-bar (ceiling/table primary) on Android within port limits: Product
    // Family is hidden + auto-assigned to 'Devio' (Tree Frog UC Platform doc
    // §2.2) — it is never user-selectable here, so re-assert it unconditionally.
    // PIG-651: without this, a design that had port-overflowed (which flips
    // `productFamily` to 'Tesira' below, keeping platform='Android') stayed
    // stuck on that fallback after the mic count dropped back under budget —
    // this `eligible` branch never promoted it back, so the equipment list kept
    // mixing the still-applicable Devio UCX bundle with the stale Tesira/TCM-X
    // à la carte lines the overflow route had added.
    area.productFamily = 'Devio';
    // A stale `_preAndroidProductFamily` snapshot can survive from an earlier
    // bar+expansion phase of this same design (captured as 'Parlé' when that
    // shape went exclusive/Android — see the `exclusive` branch above). Once
    // the primary mic changes to a non-bar shape (ceiling/table/pendant) while
    // remaining on Android, that bar-era snapshot no longer describes this
    // design at all. Left in place, a later MANUAL switch back to Windows
    // (setPlatform) falls through to "restore `_preAndroidProductFamily`" and
    // resurrects the stale 'Parlé' — which Selector.tsx then locks (disables)
    // the Product Family control on, instead of showing the real, editable
    // Tesira/Devio choice. Refresh it to 'Tesira' (the non-bar CLM baseline
    // default — same value BMA360D/ceiling-tile pins to) so a later Windows
    // switch lands on a sane, unlocked value instead of the leftover bar value.
    if (area._preAndroidProductFamily !== undefined) {
      area._preAndroidProductFamily = 'Tesira';
    }
  }

  // Family/platform are settled for this pass — enforce the Devio UCX UC
  // Compute rule before the camera/flag tail below.
  applyUcxUcComputeRule(state, area);

  const cameraPatch = computeAndroidAutoCamera(area, toolIndex[state.currentTool]?.constants);
  if (cameraPatch) {
    area.cameras = cameraPatch.camera;
    if (area.customized) area.customized.cameras = false;
    state._toastMessage = cameraPatch.toast;
    state._toastSeverity = 'warning';
  }

  // Recompute the ceiling↔tabletop switch-availability flag now
  // that platform/family/mic counts are all settled for this pass.
  area.ucxMicTypeSwitchAllowed = computeMicTypeSwitchFlag(state, area);
};

const roomSlice = {
  reducers: {
    /**
     * Sets the area orientation.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new area orientation ('lengthwise' or 'widthwise').
     */
    setAreaOrientation: (state, action) => {
      state.areas[state.activeTab].areaOrientation = action.payload
    },
    /**
     * Updates the door properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new door properties to be updated.
     */
    setDoor: (state, action) => {
      const area = state.areas[state.activeTab]
      const currentDoor = area.door;
      area.door = { ...currentDoor, ...action.payload }
    },
    /**
     * Sets the ceiling tiles properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new ceiling tiles properties.
     */
    setCeilingTiles: (state, action) => {
      try {
        // Safety checks for required dependencies
        if (!state.currentTool || !toolIndex[state.currentTool] || !state.activeTab || !state.areas[state.activeTab]) {
          console.warn('setCeilingTiles: Missing required state properties');
          return;
        }

        const currentArea = state.areas[state.activeTab];
        const toolConstants = toolIndex[state.currentTool];
        
        // Ensure we have valid initial state and constants
        if (!toolConstants.initialState?.area || !toolConstants.constants?.ceilingTiles) {
          console.warn('setCeilingTiles: Missing tool constants or initial state');
          return;
        }

        const initTile = { ...toolConstants.initialState.area(state).ceilingTiles };
        const payload = transform({ ...action.payload } as any, (result: any, value: any, key: any) => set(result, String(key).split('.'), value));
        const { tile, checked, qty } = payload as any;
        const currentCeiling = tile ? { ...initTile, tile: tile } : currentArea.ceilingTiles;
        
        if (checked) currentCeiling.checked = checked;
        
        // Validate and correct ceiling tile quantities if they exceed room capacity
        if (qty && tile && tile !== "no_ceiling_tiles") {
          const tileConstants = toolConstants.constants.ceilingTiles;
          
          if (tileConstants && tileConstants[tile] && currentArea.dimensions) {
            const { length, width } = currentArea.dimensions;
            const tileDimensions = tileConstants[tile];
            
            // Check if tile dimensions are valid
            if (tileDimensions &&
                typeof tileDimensions.x === 'number' &&
                typeof tileDimensions.y === 'number' &&
                tileDimensions.x > 0 &&
                tileDimensions.y > 0) {

              const maxX = calcMaxTilesFit(length, tileDimensions.x);
              const maxY = calcMaxTilesFit(width,  tileDimensions.y);
              
              // Correct quantities if they exceed the maximum
              if (qty && typeof qty === 'object' && 
                  typeof qty.x === 'number' && 
                  typeof qty.y === 'number' && 
                  (qty.x > maxX || qty.y > maxY)) {
                payload.qty = {
                  x: Math.max(1, Math.min(qty.x, maxX)),
                  y: Math.max(1, Math.min(qty.y, maxY))
                };
              }
            }
          }
        }
        
        // PIG-527: re-clamp a grid offset to the move range a given tile count allows.
        // Mirrors MuiCeiling's move-limit math (in room units): full coverage allows a ±1-tile
        // drag via the hidden buffer ring; partial coverage allows ±(wall-to-wall slack)/2.
        // Returns null when tile size / room dimensions are unavailable, so the caller can pick
        // its own fallback. An offset already inside the range is returned unchanged, so a grid
        // that was never moved keeps {0,0} and stays centred.
        const reconcileTransform = (
          offset: { x?: number; y?: number } | undefined,
          forQty: { x?: number; y?: number } | undefined,
        ): { x: number; y: number; a: number } | null => {
          const tileDim = toolConstants.constants.ceilingTiles?.[currentCeiling.tile];
          if (!tileDim || !(tileDim.x > 0) || !(tileDim.y > 0) || !currentArea.dimensions
              || typeof forQty?.x !== 'number' || typeof forQty?.y !== 'number') {
            return null;
          }
          const { length, width } = currentArea.dimensions;
          const maxX = calcMaxTilesFit(length, tileDim.x);
          const maxY = calcMaxTilesFit(width, tileDim.y);
          const cx = clamp(forQty.x, Math.min(1, maxX), maxX);
          const cy = clamp(forQty.y, Math.min(1, maxY), maxY);
          const drawX = cx + (cx === maxX ? 2 : 0);
          const drawY = cy + (cy === maxY ? 2 : 0);
          const movementX = cx === maxX ? tileDim.x : Math.max(0, length - drawX * tileDim.x) / 2;
          const movementY = cy === maxY ? tileDim.y : Math.max(0, width - drawY * tileDim.y) / 2;
          return {
            x: clamp(offset?.x ?? 0, -movementX, movementX),
            y: clamp(offset?.y ?? 0, -movementY, movementY),
            a: 0,
          };
        };

        // If quantity changes, reset checked tiles (their tile indices no longer map to the
        // new grid) and reconcile the grid position.
        if (qty && !isEqual(qty, currentCeiling.qty)) {
          currentCeiling.checked = [];

          // PIG-527: preserve the user's grid position instead of snapping it back to centre.
          // The old code reset transform to {0,0,0} on every count change, so a moved grid
          // jumped to centre when the user tweaked the tile count. Keep the existing offset,
          // re-clamped to the move range the NEW count allows (a larger grid has less slack,
          // so an offset valid for a smaller grid could otherwise push it out of the room).
          // A grid that was never moved has offset {0,0} and stays centred — unchanged.
          const prev = currentCeiling.transform ?? { x: 0, y: 0, a: 0 };
          const reconciled = reconcileTransform(prev, (payload.qty ?? qty) as { x: number; y: number });
          // Fallback (missing tile size / dimensions): keep the previous centre-on-change behaviour.
          payload.transform = reconciled ?? { x: 0, y: 0, a: 0 };
        } else if (payload.transform && !tile) {
          // PIG-527 (re-open): a MOVE-only dispatch (drag release sends just `transform`, no qty)
          // must not push tiles outside the room. getCenters drops any tile whose centre lands
          // outside the room, so an over-large offset silently collapses the entered count. The
          // client (MuiCeiling) clamps the drag, but defend the reducer too: an offset from a
          // loaded design, a programmatic dispatch, or a client/reducer math drift would otherwise
          // shrink the grid. Re-clamp the incoming offset against the CURRENT count's move range,
          // so the count the user entered is preserved regardless of the move. If the tile size or
          // room dimensions are unavailable, leave the offset as sent (no safe range to clamp to).
          const reconciled = reconcileTransform(payload.transform, currentCeiling.qty);
          if (reconciled) {
            payload.transform = { ...payload.transform, x: reconciled.x, y: reconciled.y };
          }
        }

        // Get the original tile type before we modify anything
        const originalTileType = currentArea.ceilingTiles?.tile;
        
        // If this is a new tile type being added, ensure it's centered
        if (tile && tile !== originalTileType) {
          payload.transform = { x: 0, y: 0, a: 0 };
          
          // Auto-calculate tile quantities when changing tile types (including between configurations)
          if (tile !== "no_ceiling_tiles") {
            const tileConstants = toolConstants.constants.ceilingTiles;
            const tileSize = tileConstants?.[tile];
            
            if (tileSize && 
                typeof tileSize.x === 'number' && 
                typeof tileSize.y === 'number' && 
                tileSize.x > 0 && 
                tileSize.y > 0 && 
                currentArea.dimensions) {
              
              const { length, width } = currentArea.dimensions;

              const maxX = calcMaxTilesFit(length, tileSize.x);
              const maxY = calcMaxTilesFit(width,  tileSize.y);

              // Set quantity to fill the room (only if qty not explicitly provided)
              if (!qty || (typeof qty === 'object' && (!qty.x || !qty.y))) {
                payload.qty = { x: maxX, y: maxY };
              }
            }
          }
        }
        
        // Safely merge the ceiling tiles state
        const newCeilingTiles = merge({}, { ...currentCeiling }, payload);
        currentArea.ceilingTiles = newCeilingTiles;

        // Reset area if resetArea function exists.
        //
        // PIG-389: a pure tile blocking/unblocking change (only `checked` changes; tile type
        // and qty are unchanged) must NOT reset device layouts. resetArea('ceilingTiles') runs
        // resetDevices(!isMicCustom, !isSpkrCustom, …), so with a customised mic it preserves
        // the mic but WIPES the (non-customised) speaker layout — making speakers disappear when
        // a tile is blocked after the mic was moved. Blocking a tile only changes tile
        // availability; the middleware recalc + snap/relocation logic repositions devices off
        // blocked tiles without a full placement reset. Only reset on a tile-type or qty change.
        const parameter = getStoreProp(action.type);
        const isOnlyBlockingChange = checked !== undefined && tile === undefined && qty === undefined;
        if (toolConstants.resetArea && typeof toolConstants.resetArea === 'function' && !isOnlyBlockingChange) {
          toolConstants.resetArea(state, parameter);
        }
      } catch (error) {
        console.error('Error in setCeilingTiles reducer:', error);
        // Fail silently to prevent app crash, but log the error
      }
    },
    /**
     * Sets the table properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new table properties.
     */
    setTable: (state, action: PayloadAction<any>) => {
      const area = state.areas[state.activeTab]
      // Safety check: ensure area exists
      if (!area) {
        console.error('No active area found for setTable');
        return;
      }
      
      
      const initTable = { ...toolIndex[state.currentTool].initialState.area(state).table };
      
      // Ensure area.table exists with proper structure
      if (!area.table || typeof area.table !== 'object') {
        area.table = initTable;
      }
      
      let payload = transform({ ...action.payload } as any, (result: any, value: any, key: any) => set(result, String(key).split('.'), value));
      const { shape = null, size = {}, chair, isGhosted } = payload as any;
      let currentTable = area.table.shape === null && shape ? initTable : area.table;
      const defaultTable = toolIndex[state.currentTool].constants.tables[shape ?? currentTable.shape] ?? {};
      const tableMaximumSize = { ...toolIndex[state.currentTool].constants.tableMaximumSize };
      currentTable = shape ? {
        ...initTable,
        shape: shape,
        size: defaultTable.default(current(area.dimensions))
      } : has(payload, 'shape') && isNull(shape) ? initTable : currentTable;

      if (chair) {
        if (isGhosted && currentTable.ghostChairs.includes(chair)) {
          currentTable.ghostChairs.splice(currentTable.ghostChairs.indexOf(chair), 1)
        } else if (!isGhosted && !currentTable.ghostChairs.includes(chair)) {
          currentTable.ghostChairs.push(chair);
        }
        payload = omit(payload, ['chair', 'isGhosted'])
      };

      let emptyGhostCHairs = false;
      const needsUpdate = !isEmpty(size) && every(keys(size), key => (has(currentTable.size, key) && Math.abs(size[key] - currentTable.size[key]) > 0.001));

      if (currentTable.shape && needsUpdate) { //!isEmpty(size)        
        payload.transform = initTable.transform;
        emptyGhostCHairs = true;

        const roomBasedMaximum = mapValues(area.dimensions, x => x - 1.4);
        
        // Get minimum dimensions for the current table shape
        const tableMinimum = defaultTable.minimum || { length: 0.9144, width: 0.9144 };

        // Check if dimensions indicate a vertical table orientation
        const isVertical = (size.width ?? currentTable.size.width) > (size.length ?? currentTable.size.length);
        if (isVertical) [tableMaximumSize.length, tableMaximumSize.width] = [tableMaximumSize.width, tableMaximumSize.length];
        // set the size maximum limit
        const maximum = mergeWith({}, tableMaximumSize, roomBasedMaximum, (objValue, srcValue) => {
          if (isNumber(objValue) && isNumber(srcValue)) {
            return Math.min(objValue, srcValue);
          }
        });

        // Validate the table size
        switch (currentTable.shape) {

          case 'racetrack':
            payload.size = {
              length: size.length ? clamp(size.length, defaultTable.minimum.length, maximum.length) : currentTable.size.length,
              width: (size.width || size.length) ? clamp((size.width ?? currentTable.size.width), defaultTable.minimum.width, Math.min((size.length ?? currentTable.size.length), maximum.width)) : currentTable.size.width
            };
            break;
          case 'boat':
            const adjustedLength = size.width ? currentTable.size.length : max([defaultTable.minimum.length, size.length]);
            const adjustedWidth = size.length ? currentTable.size.width : max([defaultTable.minimum.width, size.width]);
            payload.size = {
              length: clamp(size.length ?? currentTable.size.length, defaultTable.minimum.length, maximum.length),
              width: clamp(adjustedLength > adjustedWidth * 5 ? adjustedLength * .2 : adjustedWidth, defaultTable.minimum.width, maximum.width)
            };
            break;
          case 'round':
            payload.size = {
              length: clamp(size.length ?? currentTable.size.length, defaultTable.minimum.length, min([max(values(tableMaximumSize)), ...values(roomBasedMaximum)])),
              width: clamp(size.length ?? currentTable.size.length, defaultTable.minimum.length, min([max(values(tableMaximumSize)), ...values(roomBasedMaximum)]))
            };
            break;
          case 'bullet':
            payload.size = {
              length: size.length ? clamp(size.length, defaultTable.minimum.length, maximum.length) : currentTable.size.length,
              width: size.width ? clamp(size.width, defaultTable.minimum.width, min([maximum.width, currentTable.size.length * 1.94])) : currentTable.size.width
            };
            break;
          case 'ushape':
          case 'vshape':
            payload.size = mapValues(size, (v, k) => clamp(v, defaultTable.minimum[k], roomBasedMaximum[k]));
            break;
          default:
            payload.size = mapValues(size, (v, k) => clamp(v, defaultTable.minimum[k], maximum[k]));
        };
      };


      const newTable = merge({}, { ...currentTable }, payload);
      newTable.ghostChairs = emptyGhostCHairs ? [] : currentTable.ghostChairs;
      area.table = newTable;


      const parameter = getStoreProp(action.type);
      toolIndex[state.currentTool].resetArea(state, parameter, action.payload);

    },
    /**
     * Sets the microphones properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new microphones properties.
     */
    setMicrophones: (state, action) => {
      const area = state.areas[state.activeTab];
      const currentMics = area.microphones;
      // Captured BEFORE migration/mutation below — used only to detect a
      // primary-TYPE change (spec §2.1) once the new model lands.
      const previousPrimaryMicType = getPrimaryMicType(currentMics.model);

      // Migrate old model keys → new product keys using config modelName as the lookup
      const micConfigs = toolIndex[state.currentTool].constants.equipment.microphones;
      const migrateMicModel = (model: string, modelName?: string): string => {
        if (micConfigs[model]) return model;
        return Object.keys(micConfigs).find(key => micConfigs[key].modelName === modelName) ?? model;
      };
      if (currentMics.model) currentMics.model = migrateMicModel(currentMics.model, currentMics.modelName);

      const newMic = { ...action.payload };
      if (newMic.model) newMic.model = migrateMicModel(newMic.model, newMic.modelName);
      const micChange = 'model' in newMic;
      const modelNameChange = 'modelName' in newMic && !micChange;

      // Spec §2.1: reset androidExplicitlySelected on a genuine primary
      // microphone TYPE change (Bar/Ceiling/Ceiling Tile/Pendant/Table) —
      // not a same-type model swap (e.g. one bar model to another, or a
      // coverage/height tweak with no model change at all). Expansion mic
      // type (dcm_x/ttm_x under a Bar primary) is a separate field, set by
      // setExpansionMicType, and never reaches this reducer.
      if (micChange && getPrimaryMicType(newMic.model) !== previousPrimaryMicType) {
        area.androidExplicitlySelected = false;
      }

      // Reset customized flags and layout when microphone model changes to ensure new type uses default position
      if (micChange || modelNameChange) {
        area.customized.microphones = false;
        area.customized.speakers = false;
        // Clear layouts to force recalculation at default positions
        area.microphones.layout = [];
        area.speakers.layout = [];
        // Clear stale plotData so the new model's speaker plot is recalculated
        area.speakers.plotData = {};
      }

      // WDT-89 AC #2: wall speakers are not valid when a bar is present. The mounting
      // selector and SpeakerMountingSelector both hide for bar mics, so once the user
      // switches to a bar there's no UI path to migrate stale wall config — without
      // this reset the calc would keep placing wall speakers invisibly. Mirrors the
      // model/mountingType rewrite that `setSpeakerMountingType` performs on an
      // explicit mounting change. Scoped to `micChange` so a height/coverage tweak
      // on an already-mismatched state doesn't fire it.
      if (micChange && MIC_CATEGORIES.BAR.has(newMic.model) && area.speakers.mountingType === 'wall') {
        area.speakers.mountingType = 'ceiling';
        // Pin to `ceilingspkr` (Desono C-IC6) — the design default in initialState.js.
        // The earlier `Object.keys().find()` was order-dependent: V8 happens to iterate
        // in insertion order today, but if a new ceiling model gets added at the top of
        // the speakers config the migration would silently land on a different default.
        // An explicit key is deterministic and matches what the user would see on a
        // fresh design.
        const constants = toolIndex[state.currentTool].constants;
        const fallback = constants.equipment.speakers.ceilingspkr;
        if (fallback) {
          area.speakers.model = 'ceilingspkr';
          area.speakers.modelName = fallback.modelName;
        }
      }

      const model = (micChange ? newMic : currentMics).model;
      const defMic = toolIndex[state.currentTool].constants.equipment.microphones[model];
      const ceilingHeight = area.ceilingHeight.min;

      // Handle ceiling microphone height logic (includes ceiling-mounted bars like CBC 2500)
      if (MIC_CATEGORIES.CEILING_MOUNTED.has(model)) {
        let calculatedHeight;

        if (micChange) {
          // When changing to a ceiling mic (tcm_x/dcm_x) or ceiling-mounted bar (CBC 2500), or
          // switching between bar models: use the model's own canonical height() formula
          // (constants.js) rather than a literal ceiling height. Each model knows its own rule —
          // tcm_x/dcm_x mount FLUSH unless the acoustics cap pulls them down
          // (computeCeilingMicHeightInfo); CBC bars apply calculateCBCHeight's ceiling−0.5'
          // clamped 6'-12' rule; BMA360D caps at its 20' spec install max.
          calculatedHeight = defMic?.height ? defMic.height(area) : ceilingHeight;
        } else if ('height' in newMic) {
          // Manual height adjustment - snap to pole increments for pole-mount devices
          // CBC 2500 CM (cable mount) allows any height; tcm_x and CBC 2500 PM snap to increments
          const usesPoles = MIC_CATEGORIES.POLE_MOUNT.has(model);

          if (usesPoles) {
            calculatedHeight = snapToPoleIncrement(newMic.height);
          } else {
            // For cable mount bars (CBC 2500 CM), allow any height
            calculatedHeight = newMic.height;
          }
        } else if ('ceilingHeight' in newMic) {
          // Ceiling height changed - recalculate height for the current model. `area` here
          // already reflects the new ceiling height (setCeilingHeight commits before this
          // dispatch — see FloorplanControls.tsx), so defMic.height(area) reads the fresh
          // value. Every ceiling-mounted model goes through its own height() rather than the raw
          // new ceiling value: CBC bars via calculateCBCHeight, BMA360D via its 20' cap, and
          // tcm_x/dcm_x via computeCeilingMicHeightInfo — which returns the ceiling itself
          // (flush) unless the room's acoustics tier requires a lower mount.
          calculatedHeight = defMic?.height ? defMic.height(area) : newMic.ceilingHeight;
        } else {
          // Keep current height for other changes
          calculatedHeight = currentMics.height;
        }

        // Determine if microphone is ceiling-mounted or pole-mounted
        const isAtCeiling = Math.abs(calculatedHeight - ceilingHeight) < 0.0001;

        // Check if heightRange is defined - show error if missing
        if (!defMic?.heightRange) {
          state._toastMessage = `Microphone configuration error: heightRange not defined for ${model || 'unknown microphone'}`;
          state._toastSeverity = 'error';
          console.error('[ERROR setMicrophones] heightRange not defined for microphone config:', model);
          return;
        }

        const heightRange = defMic.heightRange(area);
        const minHeight = heightRange[0];
        const maxHeight = heightRange[1];

        // Validate and clamp height for all ceiling microphones
        let finalHeight = calculatedHeight;
        const originalInput = 'height' in newMic ? newMic.height : null;

        // Clamp height to valid range (applies to all ceiling mics including bars)
        if (calculatedHeight < minHeight) {
          finalHeight = minHeight;
        } else if (calculatedHeight > maxHeight) {
          finalHeight = maxHeight;
        }
        
        // Store original input for validation if it was invalid
        if (originalInput !== null && !micChange && originalInput !== finalHeight) {
          const isInvalidInput = originalInput < minHeight || originalInput > ceilingHeight;
          if (isInvalidInput) {
            area._originalHeightInput = originalInput;
          } else if (area._originalHeightInput !== undefined) {
            delete area._originalHeightInput;
          }
        } else if (originalInput !== null && originalInput === finalHeight && area._originalHeightInput !== undefined) {
          delete area._originalHeightInput;
        }
        
        newMic.height = finalHeight;

        // Manage pole accessories based on mounting type
        const isCeilingMounted = Math.abs(finalHeight - ceilingHeight) < 0.0001;
        if (!isCeilingMounted) {
          const poleLength = ceilingHeight - finalHeight;

          // Store pole length info for equipment list calculation during resetArea
          const micQty = area.microphones.qty || 1;
          area._poleRequired = {
            length: poleLength,
            micCount: micQty
          };
        } else {
          // Clear any pole requirements when ceiling mounted
          if (area._poleRequired) {
            delete area._poleRequired;
          }
        }
        
      } else {
        // Handle non-ceiling microphones (pendant, etc.)
        const heightRange = defMic?.heightRange ? defMic.heightRange(area) : [1.8288, 10];

        // For pendant mics, if current height equals ceiling height, reset to 8 feet
        const isPendantSwitch = micChange && MIC_CATEGORIES.PENDANT.has(newMic.model);
        const isPendantStaying = !micChange && MIC_CATEGORIES.PENDANT.has(currentMics.model);
        const isAtCeilingHeight = Math.abs((currentMics.height ?? 0) - area.ceilingHeight.min) < 0.01;
        // Reset to 8' when: switching TO pendant from ceiling height, OR staying on pendant with ceiling height
        const shouldResetToPendantHeight = (isPendantSwitch || isPendantStaying) && isAtCeilingHeight;

        const calculatedHeight = (micChange || shouldResetToPendantHeight) && defMic?.height ?
          defMic.height(area) :
          (newMic.height ?? currentMics.height);

        // Clamp to valid range
        const safeHeight = typeof calculatedHeight === 'number' && !isNaN(calculatedHeight) ? calculatedHeight : 2.4384;
        const clampedHeight = clamp(safeHeight, heightRange[0], heightRange[1]);
        
        newMic.height = clampedHeight;
      }
      
      // Auto-set the correct modelName when microphone type changes, but only if not explicitly provided
      if (micChange && !newMic.modelName && defMic?.modelName) {
        newMic.modelName = defMic.modelName;
      }
      
      // Handle modelName changes for bar microphones (ABC 2500a vs VBC 2500a vs CBC 2500)
      if (!micChange && 'modelName' in newMic && MIC_CATEGORIES.BAR.has(currentMics.model)) {
        // Bar model changed — reset expansion mic/speaker decision so dialog re-evaluates
        area.expansionMicType = null;
        area.expansionMicRecommended = false;
        area.expansionMicCoverage = null;
        area.expansionMicrophones = null;
        area.expansionSpeakerType = null;
        area.expansionSpeakers = null;
        if (area.customized) area.customized.expansionMicrophones = false;
        // Resolve the target product key from the new modelName so camera logic
        // is driven by the TARGET bar type, not the current one.
        const allMicConfigs = toolIndex[state.currentTool].constants.equipment.microphones;
        const targetKey = Object.keys(allMicConfigs).find(k => allMicConfigs[k].modelName === newMic.modelName) ?? currentMics.model;
        const isAudioBar = !MIC_CATEGORIES.HAS_CAMERA.has(targetKey);
        const isVideoBar = MIC_CATEGORIES.HAS_CAMERA.has(targetKey);

        // Keep the product key in sync with the new modelName
        area.microphones.model = targetKey;

        // Ensure hasBar remains true for any bar microphone type
        area.hasBar = true;

        if (isAudioBar) {
          // Switching to ABC 2500a or CBC 2500 PM audio bar - default to vidi280 camera
          const defaultCameraModel = 'vidi280';
          const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[defaultCameraModel];
          if (cameraEquipment) {
            area.cameras = {
              hasCamera: true,
              model: defaultCameraModel,
              modelName: cameraEquipment.modelName,
              coverageRadius: cameraEquipment.coverageRadius,
              coverageAngle: cameraEquipment.coverageAngle,
              layout: [],
              qty: 0,
              plotData: {}, // Clear plotData for audio bar
              // Initialize plotVisibility for dual-imager cameras (Vidi 280) - default to Wide
              plotVisibility: {
                wide_only: true,
                zoom_only: false
              }
            };
          } else {
            // Fallback to none if vidi280 not available
            area.cameras = {
              hasCamera: true, // Keep true to maintain nav visibility
              coverageRadius: 0,
              coverageAngle: 0,
              model: 'none',
              modelName: 'None',
              layout: [],
              qty: 0,
              plotData: {} // Clear plotData for audio bar
            };
          }
          area.customized.cameras = false;
        } else if (isVideoBar) {
          // Switching to video bar - use the resolved targetKey's cameraKey
          const cameraModelKey = allMicConfigs[targetKey]?.cameraKey ?? 'barCamera';
          const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[cameraModelKey];
          if (cameraEquipment) {
            area.cameras = {
              hasCamera: true,
              model: cameraModelKey,
              modelName: cameraEquipment.modelName,
              coverageRadius: cameraEquipment.coverageRadius,
              coverageAngle: cameraEquipment.coverageAngle,
              layout: [],
              qty: 0,
              plotData: {} // Clear plotData for video bar
            };
            area.customized.cameras = false;
          }
        }
      }
      
      // Handle camera state when switching microphone types
      if (micChange) {
        const wasBarMic = MIC_CATEGORIES.BAR.has(currentMics.model);
        const isBarMic = MIC_CATEGORIES.BAR.has(model);
        const isVideoBar = MIC_CATEGORIES.HAS_CAMERA.has(model);
        
        // Set hasBar flag based on microphone type
        area.hasBar = isBarMic;

        // Reset expansion mic/speaker state whenever the primary mic type changes
        area.expansionMicType = null;
        area.expansionMicRecommended = false;
        area.expansionMicCoverage = null;
        area.expansionMicrophones = null;
        area.expansionSpeakerType = null;
        area.expansionSpeakers = null;
        if (area.customized) area.customized.expansionMicrophones = false;

        // Reset camera state when switching away from bar microphones
        if (wasBarMic && !isBarMic) {
          // Set default standalone camera (vidi280) when switching away from bar mics
          const defaultCameraModel = 'vidi280';
          const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[defaultCameraModel];
          if (cameraEquipment) {
            area.cameras = {
              hasCamera: true,
              model: defaultCameraModel,
              modelName: cameraEquipment.modelName,
              coverageRadius: cameraEquipment.coverageRadius,
              coverageAngle: cameraEquipment.coverageAngle,
              layout: [],
              qty: 0,
              plotData: {}, // Clear plotData when switching from bar to non-bar mic
              // Initialize plotVisibility for dual-imager cameras (Vidi 280) - default to Wide
              plotVisibility: {
                wide_only: true,
                zoom_only: false
              }
            };
          } else {
            // Fallback to none if vidi280 not available
            area.cameras = {
              hasCamera: true,
              coverageRadius: 0,
              coverageAngle: 0,
              model: 'none',
              modelName: 'None',
              layout: [],
              qty: 0,
              plotData: {} // Clear plotData
            };
          }
          area.customized.cameras = false;
        }
        // Set up camera for video bar microphones
        else if (!wasBarMic && isBarMic && isVideoBar) {
          // Auto-configure camera for video bar using the cameraKey from mic config
          const cameraModelKey = defMic?.cameraKey ?? 'barCamera';
          const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[cameraModelKey];
          if (cameraEquipment) {
            area.cameras = {
              hasCamera: true,
              model: cameraModelKey,
              modelName: cameraEquipment.modelName,
              coverageRadius: cameraEquipment.coverageRadius,
              coverageAngle: cameraEquipment.coverageAngle,
              layout: [],
              qty: 0,
              plotData: {} // Clear plotData for video bar
            };
            area.customized.cameras = false;
          }
        }
        // Set up camera for audio bar microphones
        else if (!wasBarMic && isBarMic && !isVideoBar) {
          // Auto-configure vidi280 camera for audio bar
          const defaultCameraModel = 'vidi280';
          const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[defaultCameraModel];
          if (cameraEquipment) {
            area.cameras = {
              hasCamera: true,
              model: defaultCameraModel,
              modelName: cameraEquipment.modelName,
              coverageRadius: cameraEquipment.coverageRadius,
              coverageAngle: cameraEquipment.coverageAngle,
              layout: [],
              qty: 0,
              plotData: {}, // Clear plotData for audio bar
              // Initialize plotVisibility for dual-imager cameras (Vidi 280) - default to Wide
              plotVisibility: {
                wide_only: true,
                zoom_only: false
              }
            };
            area.customized.cameras = false;
          }
        }
        // Handle switching between bar types (video bar to audio bar or vice versa)
        else if (wasBarMic && isBarMic) {
          if (isVideoBar) {
            // Switching to video bar - use cameraKey from mic config
            const cameraModelKey = defMic?.cameraKey ?? 'barCamera';
            const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[cameraModelKey];
            if (cameraEquipment) {
              area.cameras = {
                ...area.cameras,
                hasCamera: true,
                model: cameraModelKey,
                modelName: cameraEquipment.modelName,
                coverageRadius: cameraEquipment.coverageRadius,
                coverageAngle: cameraEquipment.coverageAngle,
                plotData: {} // Clear plotData when switching to video bar
              };
            }
          } else {
            // Switching to audio bar - default to vidi280 camera
            const defaultCameraModel = 'vidi280';
            const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[defaultCameraModel];
            if (cameraEquipment) {
              area.cameras = {
                hasCamera: true,
                model: defaultCameraModel,
                modelName: cameraEquipment.modelName,
                coverageRadius: cameraEquipment.coverageRadius,
                coverageAngle: cameraEquipment.coverageAngle,
                layout: [],
                qty: 0,
                plotData: {}, // Clear plotData when switching to audio bar
                // Initialize plotVisibility for dual-imager cameras (Vidi 280) - default to Wide
                plotVisibility: {
                  wide_only: true,
                  zoom_only: false
                }
              };
            } else {
              // Fallback to none if vidi280 not available
              area.cameras = {
                hasCamera: true, // Keep true to maintain nav visibility
                coverageRadius: 0,
                coverageAngle: 0,
                model: 'none',
                modelName: 'None',
                layout: [],
                qty: 0,
                plotData: {} // Clear plotData when switching to audio bar
              };
            }
            area.customized.cameras = false;
          }
        }
      }
      
      // Handle coverage state to maintain proper structure for MuiToggleSelect
      if (micChange && !('coverage' in newMic)) {
        const defaultCoverage = defMic?.coverage?.(area) ?? currentMics.coverage;
        // Ensure coverage state maintains the expected object structure for toggleSelect component
        if (typeof defaultCoverage === 'object' && defaultCoverage !== null) {
          newMic.coverage = { ...defaultCoverage };
          
          // Special handling for tabletop_mic and bar which have all false coverage values
          // but still need one option to appear active in the UI
          const hasAnyTrue = Object.values(newMic.coverage).some(value => value === true);
          if (!hasAnyTrue) {
            // For cases like tabletop_mic where all coverage options are false,
            // set focused to true to ensure proper UI button styling
            // This aligns with the noSelect: 'focused' setting in the menu configuration
            newMic.coverage.focused = true;
          }
        } else {
          // If defaultCoverage returns a string or other format, convert to expected object structure
          // Preserve the current coverage structure and only update the active selection
          newMic.coverage = { ...currentMics.coverage };
          // Reset all to false first, then set the default one to true
          Object.keys(newMic.coverage).forEach(key => {
            newMic.coverage[key] = false;
          });
          // Set the default coverage option to true
          if (typeof defaultCoverage === 'string' && newMic.coverage.hasOwnProperty(defaultCoverage)) {
            newMic.coverage[defaultCoverage] = true;
          } else {
            // Fallback to focused if defaultCoverage format is unexpected
            newMic.coverage.focused = true;
          }
        }
      } else {
        newMic.coverage = newMic.coverage ?? currentMics.coverage;
      }
      
      if (has(newMic, 'coverageDensity.value')) {
        newMic.qty = newMic.coverageDensity.value;
        // Clear customized flag when coverage density changes via slider
        // This ensures the layout is recalculated with the new quantity
        if (area.customized) {
          area.customized.microphones = false;
        }
      }
      newMic.coverageDensity = { ...currentMics.coverageDensity, ...newMic?.coverageDensity };

      // Clear plotData for model/modelName changes or height changes on ceiling-mounted bars
      // This ensures plots are recalculated even when resetArea preserves layouts
      const isCeilingMountedBar = MIC_CATEGORIES.CBC.has(model);
      const isHeightChange = 'height' in newMic;
      const shouldClearPlotData = micChange || modelNameChange || (isHeightChange && isCeilingMountedBar);

      if (shouldClearPlotData) {
        newMic.plotData = {};
      }

      area.microphones = { ...currentMics, ...newMic };

      // Only clear speaker plotData when switching to/from bar microphones
      // Bar speakers are linked to bar microphones, but ceiling/pendant/table speakers are independent
      const isBarMicChange = micChange && (MIC_CATEGORIES.BAR.has(currentMics.model) || MIC_CATEGORIES.BAR.has(model));

      if (isBarMicChange) {
        area.speakers.plotData = {};
      }

      // Only call resetArea for significant changes that require recalculation
      // For ceiling-mounted bars (CBC 2500), height changes DO need recalculation because Z coordinate affects plots
      const shouldResetArea = 'model' in newMic || 'qty' in newMic || 'coverage' in newMic ||
                              (isHeightChange && isCeilingMountedBar);
      if (shouldResetArea) {
        const parameter = getStoreProp(action.type)
        // Pass the old model so resetArea can detect type changes
        toolIndex[state.currentTool].resetArea(state, parameter, {
          ...action.payload,
          _oldModel: currentMics.model  // Capture old model before state update
        })
      }

      revalidateUcxEligibility(state);
    },
    /**
     * Sets the acoustics property.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new acoustics value.
     */
    setAcoustics: (state, action) => {
      if (state.areas[state.activeTab].acoustics === action.payload) return;
      state.areas[state.activeTab].acoustics = action.payload;
      const parameter = getStoreProp(action.type);
      toolIndex[state.currentTool].resetArea(state, parameter);
    },
    /**
     * Sets the speakers properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new speakers properties.
     */
    setSpeakers: (state, action) => {
      const area = state.areas[state.activeTab];
      const currentSpkrs = area.speakers;
      const newSpeaker = { ...action.payload };

      // Migration: Add default values for new properties if they don't exist
      // This ensures backward compatibility with designs created before wall speakers
      if (currentSpkrs.mountingType === undefined) {
        currentSpkrs.mountingType = 'ceiling';
      }
      if (currentSpkrs.linkedSpeakers === undefined) {
        currentSpkrs.linkedSpeakers = true;
      }
      if (currentSpkrs.targetPoint === undefined) {
        // Initialize target point based on table/chair configuration
        const constants = toolIndex[state.currentTool].constants;
        currentSpkrs.targetPoint = calculateWallSpeakerTargetPoint(area, constants);
      }
      if (currentSpkrs.showAimingAngles === undefined) {
        currentSpkrs.showAimingAngles = false;
      }

      // If modelName is provided but not model, find the matching model key
      if ('modelName' in newSpeaker && !('model' in newSpeaker)) {
        const constants = toolIndex[state.currentTool].constants;
        const currentMountingType = currentSpkrs.mountingType || 'ceiling';

        // First try to find a model that matches both modelName AND mountingType
        let matchingModel = Object.keys(constants.equipment.speakers).find(
          key => {
            const speaker = constants.equipment.speakers[key];
            const speakerMountingType = speaker.mountingType ||
              (speaker.isCeilingSpkr ? 'ceiling' :
               speaker.isPendantSpkr ? 'pendant' :
               speaker.isWallSpkr ? 'wall' : 'ceiling');

            return speaker.modelName === newSpeaker.modelName &&
                   speakerMountingType === currentMountingType;
          }
        );

        // If no match found with mounting type, fall back to just modelName match
        if (!matchingModel) {
          matchingModel = Object.keys(constants.equipment.speakers).find(
            key => constants.equipment.speakers[key].modelName === newSpeaker.modelName
          );
        }

        if (matchingModel) {
          newSpeaker.model = matchingModel;
          // Update height based on the new model
          newSpeaker.height = constants.equipment.speakers[matchingModel].height(area);
        }
      }

      const spkrChange = 'model' in newSpeaker;
      const model = (spkrChange ? newSpeaker : currentSpkrs).model;
      const defSpkr = toolIndex[state.currentTool].constants.equipment.speakers[model];

      // Handle coverage
      newSpeaker.coverage = spkrChange ? (newSpeaker.coverage ?? defSpkr.coverage(area)) : (newSpeaker.coverage ?? currentSpkrs.coverage);

      // Handle height for pendant and wall speakers
      if (spkrChange && defSpkr?.height) {
        // When changing speaker model, set height from constants
        newSpeaker.height = defSpkr.height(area);
      } else if ('height' in newSpeaker && defSpkr?.heightRange) {
        // When manually adjusting height, clamp to valid range
        const heightRange = defSpkr.heightRange(area);
        const clampedHeight = clamp(newSpeaker.height, heightRange[0], heightRange[1]);
        newSpeaker.height = clampedHeight;

        // Update height in existing speaker layout so tooltip shows updated value
        if (area.speakers.layout && area.speakers.layout.length > 0) {
          area.speakers.layout = area.speakers.layout.map(speaker => ({
            ...speaker,
            height: clampedHeight
          }));

          // For wall, pendant, and ceiling speakers, recalculate aiming angles with new height
          if (defSpkr?.isWallSpkr) {
            const constants = toolIndex[state.currentTool].constants;
            const targetPoint = currentSpkrs.targetPoint;
            area.speakers.layout.forEach(speaker => {
              const angles = calculateAimingAngles(speaker.location, targetPoint, clampedHeight, constants);
              speaker.horAngle = angles.horAngle;
              speaker.verAngle = angles.verAngle;
            });
          } else if (defSpkr?.isPendantSpkr || defSpkr?.isCeilingSpkr) {
            const constants = toolIndex[state.currentTool].constants;

            area.speakers.layout.forEach(speaker => {
              // Calculate vertical angle for downward aim (straight down = -90°)
              const listenerHeight = constants.area.speakerAiming.listenerHeight;
              const heightDiff = clampedHeight - listenerHeight;
              const minDistance = 0.001; // Very small distance for nearly vertical aim
              let verAngle = -(Math.atan2(heightDiff, minDistance) * 180) / Math.PI;

              // Constrain vertical angle
              verAngle = Math.max(
                constants.area.speakerAiming.minVerticalAngle,
                Math.min(constants.area.speakerAiming.maxVerticalAngle, verAngle)
              );

              speaker.horAngle = 90;
              speaker.verAngle = Math.round(verAngle * 10) / 10;
            });
          }
        }
      }

      // Handle mounting type change
      if ('mountingType' in newSpeaker && newSpeaker.mountingType === 'wall') {
        // When switching to wall mounting, enable default settings
        newSpeaker.linkedSpeakers = true;
        newSpeaker.showAimingAngles = false;
      }

      // Handle nested targetPoint property updates (targetPoint.x, targetPoint.y)
      if ('targetPoint.x' in action.payload || 'targetPoint.y' in action.payload) {
        // Merge nested targetPoint properties
        const updatedTargetPoint = {
          ...currentSpkrs.targetPoint,
          ...('targetPoint.x' in action.payload && { x: action.payload['targetPoint.x'] }),
          ...('targetPoint.y' in action.payload && { y: action.payload['targetPoint.y'] })
        };


        // Update based on linked state
        if (currentSpkrs.linkedSpeakers) {
          // Linked mode: update shared target point
          newSpeaker.targetPoint = updatedTargetPoint;
        } else {
          // Unlinked mode: update each speaker's individual target point
          if (area.speakers.layout && area.speakers.layout.length > 0) {
            // Update individual target points for each speaker
            area.speakers.layout = area.speakers.layout.map(speaker => ({
              ...speaker,
              targetPoint: updatedTargetPoint
            }));
          }
          // Also update the shared one for consistency
          newSpeaker.targetPoint = updatedTargetPoint;
        }

        // Mark speakers as customized since target point was manually adjusted
        if (area.customized) {
          area.customized.speakers = true;
        }

        // For wall speakers, recalculate aiming angles when target point changes
        if (defSpkr?.isWallSpkr && area.speakers.layout) {
          const constants = toolIndex[state.currentTool].constants;
          area.speakers.layout.forEach(speaker => {
            const angles = calculateAimingAngles(speaker.location, updatedTargetPoint, speaker.height, constants);
            speaker.horAngle = angles.horAngle;
            speaker.verAngle = angles.verAngle;
          });
        }
      }

      state.areas[state.activeTab].speakers = { ...currentSpkrs, ...newSpeaker };

      const parameter = getStoreProp(action.type)
      // Pass the old model so resetArea can detect type changes
      toolIndex[state.currentTool].resetArea(state, parameter, {
        ...action.payload,
        _oldModel: currentSpkrs.model  // Capture old model before state update
      })

      // A speaker MODEL change can change Devio UCX eligibility — the Devio UCX
      // bundles only carry the standard C-IC6, so e.g. wall / pendant / C-IC6LP
      // speakers on a ceiling- or table-mic Android design must fall back to
      // Tesira + UCC-UCX1000-T (and switching back to C-IC6 must restore Devio
      // UCX). Gated on an actual model change so height / aiming / target-point
      // edits don't re-run platform resolution.
      if (spkrChange && newSpeaker.model !== currentSpkrs.model) {
        revalidateUcxEligibility(state);
      }
    },
    /**
     * Sets the speaker mounting type and updates related properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload.mountingType - The new mounting type ('ceiling', 'pendant', 'wall').
     */
    setSpeakerMountingType: (state, action) => {
      const area = state.areas[state.activeTab];
      const { mountingType } = action.payload;


      // Update mounting type
      area.speakers.mountingType = mountingType;

      // Find the speaker model for this mounting type. Prefer a specific model key, but fall back
      // to the generic key when there is no specific one.
      // PIG-451: pendant has ONLY the generic 'pendantspkr' (Desono P6-SM) — no specific key like
      // wall's wallspkr_mask6c/etc. The old code skipped all generic keys, so selecting Pendant
      // found no model, silently kept the previous (ceiling) speaker, and made the P6-SM pendant +
      // ceiling-mic config unreachable (so its TCM-XA head-unit selection could never fire).
      const constants = toolIndex[state.currentTool].constants;
      const genericKeys = ['wallspkr', 'ceilingspkr', 'pendantspkr', 'ceilingspkr_lp'];
      const speakerKeys = Object.keys(constants.equipment.speakers);
      const matchesMounting = (key) => constants.equipment.speakers[key].mountingType === mountingType;
      const matchingModel = speakerKeys.find(key => !genericKeys.includes(key) && matchesMounting(key))
        ?? speakerKeys.find(key => genericKeys.includes(key) && matchesMounting(key));


      if (matchingModel) {
        area.speakers.model = matchingModel;
        area.speakers.modelName = constants.equipment.speakers[matchingModel].modelName;
        area.speakers.height = constants.equipment.speakers[matchingModel].height(area);
      }

      // When switching to wall mounting, enable default settings and initialize target point
      if (mountingType === 'wall') {
        area.speakers.linkedSpeakers = true;
        area.speakers.showAimingAngles = false;

        // Initialize target point based on table/chair configuration
        // Front wall is left side, so depth is along length (x-axis)
        area.speakers.targetPoint = calculateWallSpeakerTargetPoint(area, constants);
      }

      // Reset customization flag when changing mounting types
      // This ensures automatic placement runs for the new mounting type
      if (area.customized) {
        area.customized.speakers = false;
      }

      // Trigger recalculation by resetting layout
      area.speakers.layout = [];
      area.speakers.qty = 0;

      const parameter = getStoreProp(action.type);
      // Pass the new model and mountingType to resetArea so it doesn't overwrite our changes
      if (matchingModel) {
        toolIndex[state.currentTool].resetArea(state, parameter, {
          model: matchingModel,
          modelName: constants.equipment.speakers[matchingModel].modelName,
          mountingType
        });
      } else {
        console.warn('⚠️ setSpeakerMountingType: No matching model found for mountingType:', mountingType);
        toolIndex[state.currentTool].resetArea(state, parameter, {
          mountingType
        });
      }

      // No Devio UCX revalidation here: the layout was just cleared, so the
      // port-budget check would see 0 speakers and wrongly promote a Tesira +
      // UCC-UCX1000-T fallback design back to Devio. `updateDevice` re-runs it
      // once calculateDevicesMiddleware has placed the new speakers.
    },
    /**
     * Toggles the speaker link state (linked/unlinked).
     * @param {Object} state - The current state.
     * @param {Object} action - The action object (no payload needed).
     */
    toggleSpeakerLink: (state, action) => {
      const area = state.areas[state.activeTab];
      const wasLinked = area.speakers.linkedSpeakers;
      area.speakers.linkedSpeakers = !area.speakers.linkedSpeakers;


      // When linking previously unlinked speakers, reset to calculated default positions
      if (area.speakers.linkedSpeakers && !wasLinked) {

        // Reset to default target point based on table/chair configuration
        // Front wall is left side, so depth is along length (x-axis)
        const constants = toolIndex[state.currentTool].constants;
        area.speakers.targetPoint = calculateWallSpeakerTargetPoint(area, constants);

        // Calculate default positions immediately (don't clear layout)
        const model = area.speakers.model;
        const modelName = constants.equipment.speakers[model].name;
        const speakerHeight = area.speakers.height || constants.equipment.speakers[model].height();
        const { left = 0, top = 0 } = area.location || {};
        const { length = 6.096, width = 4.2672 } = area.dimensions || {};
        const offsetFromCorner = width / 8;
        const wallOffset = 0.15;

        // Position two speakers on front wall (left side)
        const positions = [
          {
            location: {
              x: left + wallOffset,
              y: top + offsetFromCorner
            },
            height: speakerHeight
          },
          {
            location: {
              x: left + wallOffset,
              y: top + width - offsetFromCorner
            },
            height: speakerHeight
          }
        ];

        // Create speaker layout with aiming angles and rotation
        area.speakers.layout = positions.map((pos, index) => {
          const angles = calculateAimingAngles(pos.location, area.speakers.targetPoint, pos.height, constants);
          const rotation = calculateSpeakerRotation(pos.location, length, width);

          return {
            id: `wall-speaker-${index + 1}`,
            model,
            modelName,
            location: pos.location,
            height: pos.height,
            horAngle: angles.horAngle,
            verAngle: angles.verAngle,
            targetPoint: { ...area.speakers.targetPoint },
            linked: true,
            rotation
          };
        });

        // Clear plotData to force recalculation with new default positions
        area.speakers.plotData = {};

        // Mark as not customized so automatic placement runs on next recalculation
        if (area.customized) {
          area.customized.speakers = false;
        }

        // Show toast notification (will be handled by middleware)
        state._toastMessage = 'Speakers linked. Position and aiming angles reset to default.';
      }
      // When unlinking, preserve current shared target point for both speakers
      else if (!area.speakers.linkedSpeakers && wasLinked) {

        // Each speaker in layout will maintain the current shared targetPoint
        // The individual targetPoints will be set during the next calculation
        if (area.speakers.layout && area.speakers.layout.length > 0) {
          const sharedTargetPoint = { ...area.speakers.targetPoint };
          area.speakers.layout = area.speakers.layout.map(speaker => ({
            ...speaker,
            targetPoint: { ...sharedTargetPoint }
          }));
        }
      }
    },
    /**
     * Clears the toast message from state.
     * @param {Object} state - The current state.
     */
    clearToastMessage: (state) => {
      delete state._toastMessage;
      delete state._toastSeverity;
      delete state._toastHeightMeters;
      delete state._toastMessage2;
      delete state._toastSeverity2;
      delete state._toastHeightMeters2;
    },
    /**
     * Clears the `_pendingCeilingPromotionRecalc` transient flag `promoteExpansionToPrimary`
     * sets (see its doc comment) after `calculateDevicesMiddleware` has picked it up and
     * dispatched the follow-up real, DOM-based placement pass for the newly-promoted primary
     * ceiling mic. Same transient-flag pattern as `clearToastMessage` above.
     */
    clearPendingCeilingPromotionRecalc: (state) => {
      delete state._pendingCeilingPromotionRecalc;
    },
    /**
     * Clears the `_pendingExpansionCeilingRecalc` transient flag `attemptTableToCeilingSwitch`
     * (Devio UCX capacity fallback Case 3b) sets after `calculateDevicesMiddleware` has picked it
     * up and re-dispatched the ceiling expansion selection, so the real DOM-based placement pass
     * runs for the newly-switched ceiling expansion mics. Same transient-flag pattern as
     * `clearPendingCeilingPromotionRecalc` above.
     */
    clearPendingExpansionCeilingRecalc: (state) => {
      delete state._pendingExpansionCeilingRecalc;
    },
    /**
     * Toggles the aiming angles visualization.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object (no payload needed).
     */
    toggleAimingAngles: (state, action) => {
      const area = state.areas[state.activeTab];
      area.speakers.showAimingAngles = !area.speakers.showAimingAngles;
    },
    /**
     * Updates the speaker target point for aiming calculations.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload.targetPoint - The new target point {x, y}.
     * @param {string} action.payload.speakerId - Optional speaker ID for individual target (unlinked mode).
     */
    updateSpeakerTarget: (state, action) => {
      const area = state.areas[state.activeTab];
      const { targetPoint, speakerId } = action.payload;
      const constants = toolIndex[state.currentTool].constants;

      if (speakerId) {
        // Update individual speaker target (unlinked mode)
        const speaker = area.speakers.layout.find(s => s.id === speakerId);
        if (speaker) {
          speaker.targetPoint = targetPoint;
          // Recalculate angles for this speaker
          const angles = calculateAimingAngles(speaker.location, targetPoint, speaker.height, constants);
          speaker.horAngle = angles.horAngle;
          speaker.verAngle = angles.verAngle;
        }
      } else {
        // Update shared target (linked mode)
        area.speakers.targetPoint = targetPoint;
        // Recalculate angles for all speakers
        area.speakers.layout.forEach(speaker => {
          const angles = calculateAimingAngles(speaker.location, targetPoint, speaker.height, constants);
          speaker.horAngle = angles.horAngle;
          speaker.verAngle = angles.verAngle;
        });
      }

      // Mark speakers as customized so the undo button appears
      if (area.customized) {
        area.customized.speakers = true;
      }
    },
    /**
     * Updates a speaker's position (with wall constraint and symmetric movement for linked speakers).
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload.speakerId - The ID of the speaker to move.
     * @param {Object} action.payload.location - The new location {x, y}.
     */
    updateSpeakerPosition: (state, action) => {
      const area = state.areas[state.activeTab];
      const { speakerId, location } = action.payload;
      const constants = toolIndex[state.currentTool].constants;
      const speakers = area.speakers.layout;
      const speakerIndex = speakers.findIndex(s => s.id === speakerId);

      if (speakerIndex !== -1) {
        // Constrain to wall
        const constrainedLocation = constrainToWall(location, area.length, area.width);
        speakers[speakerIndex].location = constrainedLocation;

        // If linked, move paired speaker symmetrically
        if (area.speakers.linkedSpeakers && speakers.length === 2) {
          const otherIndex = speakerIndex === 0 ? 1 : 0;
          const mirroredLocation = getMirroredLocation(constrainedLocation, area);
          speakers[otherIndex].location = mirroredLocation;

          // Recalculate aiming angles for other speaker
          const otherTargetPoint = area.speakers.linkedSpeakers
            ? area.speakers.targetPoint
            : speakers[otherIndex].targetPoint;
          const otherAngles = calculateAimingAngles(mirroredLocation, otherTargetPoint, speakers[otherIndex].height, constants);
          speakers[otherIndex].horAngle = otherAngles.horAngle;
          speakers[otherIndex].verAngle = otherAngles.verAngle;
        }

        // Recalculate aiming angles for moved speaker
        const targetPoint = area.speakers.linkedSpeakers
          ? area.speakers.targetPoint
          : speakers[speakerIndex].targetPoint;
        const angles = calculateAimingAngles(constrainedLocation, targetPoint, speakers[speakerIndex].height, constants);
        speakers[speakerIndex].horAngle = angles.horAngle;
        speakers[speakerIndex].verAngle = angles.verAngle;
      }
    },
    /**
     * Updates properties for a specific device (microphones, speakers, etc.).
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - An object with device names as keys and their new properties as values.
     */
    updateDevice: (state, action) => {
      const area = state.areas[state.activeTab];
      const isFromMiddleware = action.payload?._fromSyncMiddleware;
      // Placed speaker count before this update — see the Devio UCX revalidation at the end.
      const prevSpeakerCount = placedDeviceCount(area?.speakers);

      toPairs(action.payload as any).forEach(([device, properties]: [string, any]) => {
        if (device === '_fromSyncMiddleware') return; // Skip the middleware flag

        const currentDevice = area[device];

        if (properties && typeof properties === 'object' && 'layout' in properties) {
          const newLayout = values(properties.layout).map((deviceUpdate: any, index: number) => {
            const mergedDevice = { ...currentDevice.layout[index], ...deviceUpdate };

            // For standalone cameras, recalculate horizontal angle when location changes
            // Skip recalculation if horAngle is already provided (e.g., for bar cameras with table rotation)
            if (device === 'cameras' && deviceUpdate.location && deviceUpdate.horAngle === undefined) {
              const { location: areaLocation, dimensions, table } = area;

              // Calculate the aim point - camera should aim at room center or table center if table exists
              const aimPoint = {
                x: areaLocation.left + dimensions.length / 2 + (table?.shape && table?.transform ? (table.transform.x || 0) : 0),
                y: areaLocation.top + dimensions.width / 2 + (table?.shape && table?.transform ? (table.transform.y || 0) : 0)
              };

              // Calculate angle from camera location to aim point
              const deltaX = aimPoint.x - deviceUpdate.location.x;
              const deltaY = aimPoint.y - deviceUpdate.location.y;

              // Calculate angle in standard math coordinates (rotation convention: 0=east/right)
              const mathAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
              const horAngle = Math.round((mathAngle + 360) % 360);

              // Update the camera's horizontal angle and aim point
              mergedDevice.horAngle = horAngle;
              mergedDevice.aimPoint = aimPoint;
            }

            // For bar devices moved manually (not from middleware), recalculate horAngle.
            // Wall-mounted bars (VBC camera, ABC/VBC mic): snap to nearest wall with cardinal angles
            //   left=0°, right=180°, top=90°, bottom=270°.
            // Ceiling-mounted bars (CBC mic/speaker): aim toward table/room center.
            //
            // The model check reads from the MOVED ENTRY itself, not the slot's primary model —
            // critical for microphones, where the layout array can contain both the bar
            // (`vbc_2500a`) AND ceiling expansion mics (`dcm_x`). Using area.microphones.model
            // would incorrectly classify a dragged expansion mic as a bar and snap it to a wall.
            if (!isFromMiddleware && deviceUpdate.location) {
              const toolConstants = toolIndex[state.currentTool]?.constants;
              const model = mergedDevice.model || area[device]?.model;
              const equipmentConfig = toolConstants?.equipment?.[device]?.[model];
              const isMicBar = device === 'microphones' && MIC_CATEGORIES.BAR.has(model);
              const isBarDevice = isMicBar || equipmentConfig?.isBarSpkr || equipmentConfig?.isBarCamera;
              if (isBarDevice) {
                const { location: areaLocation, dimensions, table } = area;
                const isWallMountedBar = equipmentConfig?.isBarCamera ||
                  (isMicBar && MIC_CATEGORIES.WALL_MOUNTED.has(model)) ||
                  (equipmentConfig?.isBarSpkr && !equipmentConfig?.isCeilingMounted);
                if (isWallMountedBar) {
                  // Wall-mounted bar (VBC camera, ABC/VBC mic): snap to nearest wall
                  const WALL_OFFSET = toolConstants?.equipment?.wallMountedBarOffset ?? 0.134;
                  const leftDist = Math.abs(deviceUpdate.location.x - areaLocation.left);
                  const rightDist = Math.abs(deviceUpdate.location.x - (areaLocation.left + dimensions.length));
                  const topDist = Math.abs(deviceUpdate.location.y - areaLocation.top);
                  const bottomDist = Math.abs(deviceUpdate.location.y - (areaLocation.top + dimensions.width));
                  const minDist = Math.min(leftDist, rightDist, topDist, bottomDist);
                  if (minDist === topDist) {
                    mergedDevice.horAngle = 90;
                    mergedDevice.location = { ...mergedDevice.location, y: areaLocation.top + WALL_OFFSET };
                  } else if (minDist === bottomDist) {
                    mergedDevice.horAngle = 270;
                    mergedDevice.location = { ...mergedDevice.location, y: areaLocation.top + dimensions.width - WALL_OFFSET };
                  } else if (minDist === rightDist) {
                    mergedDevice.horAngle = 180;
                    mergedDevice.location = { ...mergedDevice.location, x: areaLocation.left + dimensions.length - WALL_OFFSET };
                  } else {
                    mergedDevice.horAngle = 0;
                    mergedDevice.location = { ...mergedDevice.location, x: areaLocation.left + WALL_OFFSET };
                  }
                } else {
                  // Ceiling-mounted bar (CBC): aim toward table/room center
                  const aimPoint = {
                    x: areaLocation.left + dimensions.length / 2 + (table?.shape && table?.transform ? (table.transform.x || 0) : 0),
                    y: areaLocation.top + dimensions.width / 2 + (table?.shape && table?.transform ? (table.transform.y || 0) : 0)
                  };
                  const deltaX = aimPoint.x - deviceUpdate.location.x;
                  const deltaY = aimPoint.y - deviceUpdate.location.y;
                  const mathAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
                  mergedDevice.horAngle = Math.round((mathAngle + 360) % 360) % 360;
                }
              }
            }

            return mergedDevice;
          });
          properties.layout = newLayout;
          properties.modelName = newLayout[0]?.modelName;

          // If layout is being updated manually (not from middleware), mark as customized
          // This prevents calculateDevicesMiddleware from recalculating positions
          if (!isFromMiddleware && device === 'speakers' && area.customized) {
            area.customized.speakers = true;
          }
          if (!isFromMiddleware && device === 'microphones' && area.customized) {
            area.customized.microphones = true;
          }
          if (!isFromMiddleware && device === 'cameras' && area.customized) {
            area.customized.cameras = true;
          }
          // Dragging an expansion mic dispatches updateDevice({ expansionMicrophones: ... }).
          // Mark expansion mics as customized so the next recalc honours the manual position
          // instead of regenerating the default placement and snapping the mic back.
          if (!isFromMiddleware && device === 'expansionMicrophones' && area.customized) {
            area.customized.expansionMicrophones = true;
          }
          // Same for expansion speakers — the move handler in MuiDevices.tsx dispatches
          // updateDevice({ expansionSpeakers: ... }) on pointer-up. The customised flag
          // is what tells `computeExpansionSpeakerCoords` to preserve the layout instead
          // of running `calcExpansionSpeakerPlacement`.
          if (!isFromMiddleware && device === 'expansionSpeakers' && area.customized) {
            area.customized.expansionSpeakers = true;
          }

          // Manually dragging a device invalidates the focused/full_room preset — hide
          // the "Focus/Full" coverage toggle until undo restores it. Set the flag on
          // `properties` (not `currentDevice`) since it's what gets merged into
          // `area[device]` below. `coverage` itself is left untouched — see
          // clearPrimaryCoverage's comment for why nulling it is unsafe.
          // Also stash the pre-drag `coverageDensity` (see clearPrimaryCoverage) so undo
          // can restore the slider position instead of the recalculated default.
          // IMPORTANT: only stash on the FIRST manual drag after a reset/undo (guarded by
          // `!currentDevice?.hideCoverageToggle`) — same reasoning as clearPrimaryCoverage:
          // by a SECOND drag, `currentDevice.coverageDensity` has already been overwritten
          // by the recalculation that followed the first drag, so re-stashing here would
          // clobber the true original value with that already-defaulted one.
          // ALSO IMPORTANT: `properties` is the raw action payload, not part of the Immer
          // draft tree — assigning a live draft reference (e.g. `currentDevice.coverageDensity`,
          // an Immer proxy) onto it leaks that proxy onto the dispatched action. Immer
          // revokes the proxy once this reducer returns, so Redux Toolkit's serializability
          // check middleware then crashes ("proxy that has been revoked") when it inspects
          // the action afterward. `current()` snapshots a plain value instead, which is
          // safe to store anywhere.
          if (!isFromMiddleware && (device === 'microphones' || device === 'speakers')) {
            if (!currentDevice?.hideCoverageToggle && currentDevice?.coverageDensity) {
              properties.lastCoverageDensity = current(currentDevice.coverageDensity);
            }
            properties.hideCoverageToggle = true;
          }

          // Dropping a manually-moved speaker invalidates the current SPL heatmap
          // immediately — clear it here so it disappears on drop rather than lingering
          // stale until HearSpace's debounced recalculation (~1-3s) delivers a fresh
          // plot via its own `_fromSyncMiddleware` follow-up dispatch (excluded above by
          // `!isFromMiddleware`, so this never clears the heatmap HearSpace just computed).
          if (!isFromMiddleware && device === 'speakers') {
            properties.plotData = {};
          }
          if (!isFromMiddleware && device === 'expansionMicrophones' && area.expansionMicType !== 'table') {
            clearExpansionCoverage(area, 'expansionMicCoverage');
          }
          if (!isFromMiddleware && device === 'expansionSpeakers' && area.expansionSpeakerType !== 'pendant') {
            clearExpansionCoverage(area, 'expansionSpeakerCoverage');
          }
        };

        const updatedDevice = { ...currentDevice, ...(properties as object) };

        area[device] = updatedDevice;

      });

      // Devio UCX eligibility depends on the placed speaker count (UCC port
      // budget). Speaker model / mounting changes clear the layout and leave the
      // real count to `calculateDevicesMiddleware`, which lands here — so this is
      // the first point the new count is known. Re-resolve Platform/Family when it
      // changes on an Android design; otherwise e.g. 3 table mics + C-IC6LP stays
      // on Devio (no Tesira DSP / amp) although it overflows the port budget.
      if (isFromMiddleware && area?.platform === 'Android'
        && action.payload?.speakers && placedDeviceCount(area.speakers) !== prevSpeakerCount) {
        revalidateUcxEligibility(state);
      }
    },

    /**
     * Adds a new device to the current area (microphones, speakers, etc.).
     * @param {Object} state - The current state of the room.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The payload of the action
     * @param {string} action.payload.device - The type of device being added (e.g., 'speakers', 'microphones')
     * @param {Object} action.payload.location - The location coordinates for the new device
     * 
     * @description
     * This function adds a new device to the layout of the currently active area. It performs the following actions:
     * 1. Retrieves the current area based on the active tab.
     * 2. Extracts the device type and location from the action payload.
     * 3. Gets the current model for the specified device type.
     * 4. Adds a new device object to the layout array of the specified device type.
     * 5. Increments the quantity count for the device type.
     * 
     * @example
     * // Dispatch an action to add a new microphone
     * dispatch(addDevice({
     *   device: 'microphones',
     *   location: { x: 10, y: 20, z: 3 }
     * }));
     * 
     */
    addDevice: (state, action) => {
      // Validate basic requirements
      if (!state.activeTab || !state.areas[state.activeTab]) {
        console.error(`No active area found: ${state.activeTab}`);
        return;
      }
      
      const area = state.areas[state.activeTab];
      const { device, location } = action.payload;
      
      // Validate device parameter
      if (!device || typeof device !== 'string') {
        console.error(`Invalid device parameter:`, device);
        return;
      }
      
      // Validate location parameter
      if (!location || typeof location.x !== 'number' || typeof location.y !== 'number') {
        console.error(`Invalid location parameter:`, location);
        return;
      }
      
      // Ensure the device configuration exists and is properly initialized
      if (!area[device] || typeof area[device] !== 'object') {
        console.error(`Device configuration for ${device} not found in area ${state.activeTab}`);
        return;
      }
      
      // Ensure layout array exists
      if (!Array.isArray(area[device].layout)) {
        area[device].layout = [];
      }
      
      // Ensure qty exists and is a number
      if (typeof area[device].qty !== 'number') {
        area[device].qty = 0;
      }
      
      // Get and validate model
      const model = area[device].model;
      if (!model || typeof model !== 'string') {
        console.error(`No valid model specified for device ${device} in area ${state.activeTab}. Current model:`, model);
        return;
      }

      // Get and validate modelname
      const modelName = area[device].modelName;
      if (!modelName || typeof modelName !== 'string') {
        console.error(`No valid model name specified for device ${device} in area ${state.activeTab}. Current model:`, modelName);
        return;
      }
      
      // Add the device
      try {
        const newDevice: any = {
          model: model,
          modelName: modelName,
          location: { ...location }
        };

        // For speakers, calculate aiming angles
        if (device === 'speakers') {
          const constants = toolIndex[state.currentTool]?.constants;
          const defSpkr = constants?.equipment?.speakers?.[model];

          if (defSpkr && (defSpkr.isPendantSpkr || defSpkr.isCeilingSpkr)) {
            const speakerHeight = area.speakers.height || (typeof defSpkr.height === 'function' ? defSpkr.height(area) : defSpkr.height);

            // Calculate vertical angle for downward aim (straight down = -90°)
            const listenerHeight = constants.area.speakerAiming.listenerHeight;
            const heightDiff = speakerHeight - listenerHeight;
            const minDistance = 0.001; // Very small distance for nearly vertical aim
            let verAngle = -(Math.atan2(heightDiff, minDistance) * 180) / Math.PI;

            // Constrain vertical angle
            verAngle = Math.max(
              constants.area.speakerAiming.minVerticalAngle,
              Math.min(constants.area.speakerAiming.maxVerticalAngle, verAngle)
            );

            newDevice.horAngle = 90;
            newDevice.verAngle = Math.round(verAngle * 10) / 10;
            newDevice.height = speakerHeight;
          }
        }

        area[device].layout.push(newDevice);
        area[device].qty = area[device].layout.length;

        // Manually adding a mic/speaker invalidates the focused/full_room preset —
        // hide the "Focus/Full" coverage toggle (see clearPrimaryCoverage) until the
        // user undoes the change.
        if (device === 'microphones' || device === 'speakers') {
          clearPrimaryCoverage(area[device]);
        }

      } catch (error) {
        console.error(`Error adding device ${device}:`, error);
      }
      revalidateUcxEligibility(state);
    },

    /**
     * Removes a specific device from the current active area in the room.
     * @param {Object} state - The current state of the room.
     * @param {Object} action - The Redux action object
     * @param {Object} action.payload - The payload of the action
     * @param {string} action.payload.device - The type of device to remove (e.g., 'speakers', 'microphones')
     * @param {number} action.payload.id - The index of the device to remove from the layout array
     * 
     * @example
     * // Dispatch an action to remove a device
     * dispatch(removeDevice({ device: 'speakers', id: 2 }));
     * 
     * @description
     * This reducer function removes a specific device from the layout of the currently active area.
     * It updates both the layout array and the quantity count for the specified device type.
     * The function assumes that the device type exists in the area object and has a 'layout' array
     * and a 'qty' property. It's typically used in response to user actions like clicking to remove
     * a device in the room designer interface.
     * 
     * @note This function mutates the state directly, which is the recommended approach
     * when using Redux Toolkit's createSlice.
     */
    removeDevice: (state, action) => {
      const area = state.areas[state.activeTab];
      const { device, id } = action.payload;
      area[device].layout.splice(id, 1);
      area[device].qty = area[device].layout.length;

      // Manually deleting a mic/speaker invalidates the focused/full_room preset —
      // hide the "Focus/Full" coverage toggle (see clearPrimaryCoverage) until the
      // user undoes the change.
      if (device === 'microphones' || device === 'speakers') {
        clearPrimaryCoverage(area[device]);
      }

      revalidateUcxEligibility(state);
    },
    
    /**
     * Moves a device to a new position.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload.device - The type of device to move
     * @param {number} action.payload.index - The index of the device in the layout array
     * @param {Object} action.payload.location - The new location coordinates
     */
    moveDevice: (state, action) => {
      const area = state.areas[state.activeTab];
      const { device, index, location } = action.payload;

      // Ensure the device layout exists
      if (!area[device].layout) {
        area[device].layout = [];
      }

      // Update existing device or create new one
      if (area[device].layout[index]) {
        area[device].layout[index].location = location;

        // Mark as customized when manually moved
        if (device === 'speakers' && area.customized) {
          area.customized.speakers = true;
        }
        if (device === 'microphones' && area.customized) {
          area.customized.microphones = true;
        }
        if (device === 'cameras' && area.customized) {
          area.customized.cameras = true;
        }

        // For pendant and ceiling speakers, recalculate aiming angles when moved
        if (device === 'speakers') {
          const constants = toolIndex[state.currentTool]?.constants;
          const speakerModel = area[device].model;
          const defSpkr = constants?.equipment?.speakers?.[speakerModel];

          if (defSpkr?.isPendantSpkr || defSpkr?.isCeilingSpkr) {
            const speaker = area[device].layout[index];

            // Calculate vertical angle for downward aim (straight down = -90°)
            const listenerHeight = constants.area.speakerAiming.listenerHeight;
            const speakerHeight = speaker.height ?? area[device].height ?? (typeof defSpkr.height === 'function' ? defSpkr.height(area) : defSpkr.height);
            const heightDiff = speakerHeight - listenerHeight;
            const minDistance = 0.001; // Very small distance for nearly vertical aim
            let verAngle = -(Math.atan2(heightDiff, minDistance) * 180) / Math.PI;

            // Constrain vertical angle
            verAngle = Math.max(
              constants.area.speakerAiming.minVerticalAngle,
              Math.min(constants.area.speakerAiming.maxVerticalAngle, verAngle)
            );

            speaker.horAngle = 90;
            speaker.verAngle = Math.round(verAngle * 10) / 10;
          }
        }

        // For cameras, recalculate horizontal angle when moved
        if (device === 'cameras') {
          const camera = area[device].layout[index];
          const { location: areaLocation, dimensions, table } = area;

          // Calculate the aim point - camera should aim at room center or table center if table exists
          const aimPoint = {
            x: areaLocation.left + dimensions.length / 2 + (table?.shape && table?.transform ? (table.transform.x || 0) : 0),
            y: areaLocation.top + dimensions.width / 2 + (table?.shape && table?.transform ? (table.transform.y || 0) : 0)
          };

          // Calculate angle from camera location to aim point
          const deltaX = aimPoint.x - location.x;
          const deltaY = aimPoint.y - location.y;

          // Calculate angle in standard math coordinates (rotation convention: 0=east/right)
          const mathAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
          const horAngle = Math.round((mathAngle + 360) % 360);

          // Update the camera's horizontal angle and aim point
          // Both are required for the selector to preserve the angle (see line 1745)
          camera.horAngle = horAngle;
          camera.aimPoint = aimPoint;
        }
      } else {
        // Create a new device entry if it doesn't exist
        area[device].layout[index] = {
          model: area[device].model,
          modelName: area[device].modelName,
          location: location
        };
        area[device].qty = area[device].layout.length;
      }
    },

    /**
     * Sets the cameras properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new cameras properties.
     */
    setCameras: (state, action) => {
      const area = state.areas[state.activeTab];
      const currentCameras = area.cameras;
      const newCamera = { ...action.payload };
      
      // Handle model change
      if ('model' in newCamera) {
        const model = newCamera.model;
        const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[model];
        
        if (model === 'none') {
          // No camera selected - keep hasCamera true to maintain UI visibility
          area.cameras = {
            hasCamera: true, // Keep true to maintain nav visibility
            coverageRadius: 0,
            coverageAngle: 0,
            model: 'none',
            modelName: 'None',
            layout: [],
            qty: 0,
            plotData: {} // Ensure plotData is cleared when no camera
          };
          area.customized.cameras = false;
        } else if (cameraEquipment) {
          // Valid camera model selected (vidi250, etc.)
          // Properly set coverage values from equipment constants
          
          // Check if we're switching from 'none' to a valid camera
          const wasNone = currentCameras.model === 'none';
          
          // IMPORTANT: Always use the coverage values from the equipment constants
          // to ensure they're correct when switching models
          // For dual-imager cameras (Vidi 280), preserve plotVisibility if available and valid
          let plotVisibility = currentCameras.plotVisibility;

          // Initialize plotVisibility for dual-imager cameras if not already set or invalid
          if (cameraEquipment.dualImager) {
            if (!plotVisibility || typeof plotVisibility !== 'object' ||
                (!plotVisibility.wide_only && !plotVisibility.zoom_only)) {
              // No valid plotVisibility exists, default to Wide
              plotVisibility = {
                wide_only: true,
                zoom_only: false
              };
            }
            // plotVisibility is valid, preserve it
          } else {
            // Single-imager camera, clear plotVisibility
            plotVisibility = undefined;
          }

          // Set coverage values based on the imager type
          const isWideSelected = plotVisibility?.wide_only;
          const selectedImager = cameraEquipment.dualImager
            ? (isWideSelected ? cameraEquipment.wideImager : cameraEquipment.zoomImager)
            : null;

          const coverageRadius = cameraEquipment.dualImager
            ? selectedImager?.coverageRadius
            : cameraEquipment.coverageRadius;
          const coverageAngle = cameraEquipment.dualImager
            ? selectedImager?.coverageAngle
            : cameraEquipment.coverageAngle;

          if (!coverageAngle && !cameraEquipment.dualImager) {
            state._toastMessage = `Camera configuration error: ${model} missing coverageAngle`;
            state._toastSeverity = 'error';
          }

          area.cameras = {
            ...currentCameras,
            hasCamera: true,
            model: model,
            modelName: cameraEquipment.modelName,
            coverageRadius: coverageRadius,
            coverageAngle: coverageAngle,
            layout: area.customized.cameras ? currentCameras.layout : [],
            qty: area.customized.cameras ? currentCameras.qty : (wasNone ? 0 : currentCameras.qty),
            plotData: {}, // Always clear plot data to force recalculation
            plotVisibility: plotVisibility
          };
          
          // Mark cameras as not customized when switching from none to a real camera
          if (wasNone) {
            area.customized.cameras = false;
          }
          
          // The middleware will handle recalculation
        }
      } else {
        // Handle other camera properties (hasCamera, etc.)
        if ('hasCamera' in newCamera && !newCamera.hasCamera) {
          area.cameras = {
            hasCamera: false, // Set to false when no camera
            coverageRadius: 0,
            coverageAngle: 0,
            model: 'none',
            modelName: 'None',
            layout: [],
            qty: 0,
            plotData: {} // Ensure plotData is cleared
          };
          area.customized.cameras = false;
        } else {
          // Update other properties while preserving existing values
          area.cameras = { ...currentCameras, ...newCamera };

          // If plotVisibility changed for a dual-imager camera, update coverage values
          if ('plotVisibility' in newCamera && currentCameras.model) {
            const cameraEquipment = toolIndex[state.currentTool].constants.equipment.cameras[currentCameras.model];
            if (cameraEquipment?.dualImager) {
              // Update coverageRadius and coverageAngle based on selected imager
              const isWideSelected = newCamera.plotVisibility?.wide_only;
              const selectedImager = isWideSelected ? cameraEquipment.wideImager : cameraEquipment.zoomImager;

              if (selectedImager) {
                area.cameras.coverageRadius = selectedImager.coverageRadius;
                area.cameras.coverageAngle = selectedImager.coverageAngle;
              }
            }
          }
        }
      }

      const parameter = getStoreProp(action.type);
      toolIndex[state.currentTool].resetArea(state, parameter, action.payload);
    },

    /**
     * Sets the product family.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new product family.
     */
    setProductFamily: (state, action) => {
      const area = state.areas[state.activeTab];
      area.productFamily = action.payload;
      // Ceiling/table mic display name follows the family (PIG-595 review).
      applyDevioMicModelName(area);
      // Devio UCX drops 'Zoom Rooms' from UC Compute (deviceConfigReducers'
      // setProductFamily — the one container.ts actually spreads last — applies
      // the same rule; kept in step here so both copies behave identically).
      applyUcxUcComputeRule(state, area);
    },

    /**
     * Sets the platform — 'Windows' or 'Android'. (UC Compute → Platform → UC device).
     * Selecting Android implies the Tree Frog UCX path, so this reducer also forces
     * `productFamily` to 'Devio' for non-bar designs (UCX bundles all carry
     * `productFamily: 'Devio'` for catalog filtering). For a single bar mic
     * design (CBC-2500, ABC-2500, VBC-2500a, VBC-2800 — no expansion), Family
     * instead resolves via `resolveBarProductFamily` (utilities/parleBarBundle.js):
     * Android + UC Compute (Teams/Zoom) → 'Devio'; Android + UC Compute = 'None'
     * → 'Parlé'. Selecting Windows resolves single-bar designs to 'Parlé'
     * (any UC Compute) and leaves non-bar `productFamily` untouched (restores
     * the pre-Android snapshot).
     *
     * @param {Object} state
     * @param {Object} action
     * @param {string|null} action.payload - 'Windows' | 'Android' | null
     */
    setPlatform: {
      /**
       * `androidExplicitlySelected` (spec §2.1) must be true ONLY for a genuine
       * direct pick from the platform selector, never for an automatic/derived
       * transition (e.g. RadioButtons.tsx's mount-time default-value effect,
       * which dispatches this same action with no meta when a bar + expansion
       * design auto-forces Android). A plain payload can't distinguish those —
       * both call sites send `{ type: 'setPlatform', payload: 'Android' }` —
       * so the direct-click call site (RadioButtons.tsx handleChange) passes
       * `{ userInitiated: true }` as a second argument, carried here via
       * `meta` rather than folded into the payload.
       */
      prepare: (value: string | null, meta?: { userInitiated?: boolean }) => ({
        payload: value,
        meta: meta ?? {},
      }),
      reducer: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      const value = action.payload;
      const prevPlatform = area.platform;

      // Gate the Android pick on UCX eligibility. Hard ineligibility (BMA360D /
      // pendant — no fallback) blocks the pick outright. Port-overflow is a SOFT
      // failure (PIG-594): Android is still valid, but the design routes its
      // peripherals through a Tesira DSP + UCC-UCX1000-T (USB) instead of the
      // Devio UCX hub (spec §5.4 "Android selected — system exceeds Tree Frog
      // port limits"). Previously overflow ALSO blocked the pick, so choosing
      // Android on an over-budget design silently did nothing.
      // Tesira-via-USB fallback reason, when Android is picked on a design that
      // can't use the Devio UCX hub directly: 'port-overflow' (PIG-594) or
      // 'speaker-incompatible' (non-bar design whose primary speaker isn't the
      // bundle C-IC6). Both keep platform=Android and route through Tesira +
      // UCC-UCX1000-T.
      let usbFallback: 'port-overflow' | 'speaker-incompatible' | null = null;
      if (value === 'Android') {
        const eligibility = isDevioUcxEligible(area);
        if (!eligibility.eligible) {
          if (eligibility.fallback === 'port-overflow' || eligibility.fallback === 'speaker-incompatible') {
            usbFallback = eligibility.fallback;
          } else {
            state._toastMessage = eligibility.suggestion
              ? `${eligibility.reason} ${eligibility.suggestion}`
              : (eligibility.reason ?? 'Devio UCX is not compatible with this configuration.');
            state._toastSeverity = 'warning';
            return;
          }
        }
      }

      // Block a manual switch to Windows when the design is exclusively Devio
      // UCX-dependent (bar mic + expansion mics and/or expansion speakers) —
      // Windows can't carry this configuration.
      if (value === 'Windows') {
        const eligibility = isDevioUcxEligible(area);
        if (eligibility.exclusive) {
          state._toastMessage = 'Windows is not available — this bar design with expansion mics/speakers requires Devio UCX (Android).';
          state._toastSeverity = 'warning';
          return;
        }
      }

      area.platform = value ?? null;
      if (value === 'Android') {
        // Snapshot pre-Android family on entry so leaving Android (auto or manual)
        // can restore the user's prior choice instead of leaving Devio in place.
        if (prevPlatform !== 'Android') {
          area._preAndroidProductFamily = area.productFamily;
        }
        // Android normally implies the Devio UCX path. On port overflow, keep
        // platform=Android but route the design through Tesira — logic.js sees
        // platform=Android + productFamily=Tesira and adds the UCC-UCX1000-T
        // compute bundle (USB) plus a Tesira DSP for the peripherals. A single
        // bar mic design instead resolves 'Devio' vs 'Parlé' based on UC
        // Compute (resolveBarProductFamily returns non-null for that shape,
        // taking precedence over the `?? …` fallback) — port overflow can't
        // happen from a bar mic itself (bar mics use USB, not Ethernet
        // peripheral chains), so the fallback only ever applies to non-bar or
        // bar+expansion designs here.
        area.productFamily = resolveBarProductFamily(area) ?? (usbFallback ? 'Tesira' : 'Devio');

        // Spec §2.1: true only for a genuine direct pick (see the `prepare`
        // callback's doc comment above) — never set by the mount-time
        // default-value effect that auto-forces Android for a bar + expansion
        // design (RadioButtons.tsx), which dispatches with no meta.
        if (action.meta?.userInitiated) {
          area.androidExplicitlySelected = true;
        }

        const patch = computeAndroidAutoCamera(area, toolIndex[state.currentTool]?.constants);
        if (patch) {
          area.cameras = patch.camera;
          if (area.customized) area.customized.cameras = false;
          state._toastMessage = patch.toast;
          state._toastSeverity = 'warning';
        }
        if (usbFallback) {
          // Surface the fallback (overrides the auto-camera toast, which still
          // applied above) so the user knows why the design routes through Tesira.
          state._toastMessage = usbFallback === 'speaker-incompatible'
            ? SPEAKER_FALLBACK_TOAST
            : PORT_OVERFLOW_FALLBACK_TOAST;
          state._toastSeverity = 'warning';
        }
      } else {
        // Windows (or unset): a single bar mic (no expansion — bar +
        // expansion is blocked from reaching Windows above) always resolves
        // to 'Parlé', regardless of UC Compute or whatever family preceded
        // it — don't restore a stale pre-Android snapshot for bar designs.
        // Non-bar designs fall through to restoring the pre-Android snapshot.
        const barFamily = resolveBarProductFamily(area);
        if (barFamily) {
          area.productFamily = barFamily;
          delete area._preAndroidProductFamily;
        } else if (prevPlatform === 'Android' && area._preAndroidProductFamily !== undefined) {
          // Leaving Android — restore the family the user had before the cascade.
          area.productFamily = area._preAndroidProductFamily;
          delete area._preAndroidProductFamily;
        }
        // Spec §2.1: explicit exit from Android — this branch is only ever
        // reached via a genuine user action (the `exclusive` guard above
        // blocks and returns before this point for a bar + expansion design
        // that auto-forces Android), so the flag no longer describes
        // anything true about this design. Reset unconditionally, not just
        // when leaving Android — also covers value === null.
        area.androidExplicitlySelected = false;
      }
      // Keep the ceiling/table mic display name (DCM-X vs TCM-X) in step with the
      // family the platform switch just settled (PIG-595 review).
      applyDevioMicModelName(area);
      // Switching to Android can make this a Devio UCX design while 'Zoom Rooms'
      // is still stored from the Windows/Parlé phase — the UC Compute list drops
      // that option here, so the value has to follow it (and switching back off
      // Devio UCX simply restores the full list, nothing to undo).
      applyUcxUcComputeRule(state, area);
      },
    },

    /**
     * Sets the equipment list.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new equipment list.
     */
    setEquipmentList: (state, action) => {
      state.areas[state.activeTab].equipmentList = action.payload
    },

    /**
     * Sets the hasBar property.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - hasbar property.
     */
    setHasBar: (state, action) => {
      state.areas[state.activeTab].hasBar = action.payload
    },

    /**
     * Resets area after the tutorial closes.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - parameters to reset.
     */
    resetTutorialChanges: (state, action) => {
      if (action.payload) {
        state.areas[state.activeTab] = { ...state.areas[state.activeTab], ...action.payload }
      }
    },

    setCollisions: (state, action) => {
      state.areas[state.activeTab].collisions = action.payload;
    },

    setExpansionMicRecommended: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      area.expansionMicRecommended = Boolean(action.payload?.recommended ?? action.payload);
    },

    setExpansionSpeakerRecommended: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      area.expansionSpeakerRecommended = Boolean(action.payload?.recommended ?? action.payload);
    },

    /**
     * "The expansion placement ran and declined because the remaining coverage gap is below the
     * minimum worth a mic" (spec edge case B / the table's below-minimum-viable Scenario 3).
     *
     * Exists because an empty layout is AMBIGUOUS. `setExpansionMicType('ceiling'|'table')` seeds
     * `{ qty: 0, layout: [] }` and the middleware fills it a few frames later, so "type set with
     * an empty layout" also describes the normal transient right after an add — deriving the
     * suppressed state from layout length would flip the Add/Remove control on every add.
     * Only the placement pass knows the difference, so it records it here.
     *
     * `expansionMicType` deliberately stays 'ceiling'/'table' while suppressed: the configuration
     * is still meaningful, and the placement restores the mics on its own once acoustics move back
     * to a tier with a worthwhile gap. What the flag changes is the UI — the control offers "Add"
     * rather than a "Remove" for mics that aren't there.
     *
     * Cleared by `setExpansionMicType` (any change) and by any `setExpansionMicLayout` that lands
     * a non-empty layout, so it cannot outlive the condition it describes.
     */
    setExpansionMicSuppressed: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      area.expansionMicSuppressed = Boolean(action.payload?.suppressed ?? action.payload);
    },

    setExpansionMicType: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      const type = typeof action.payload === 'string' ? action.payload : action.payload?.type;
      area.expansionMicType = type ?? null;

      // Any type change re-opens the question the flag answers — the new type's placement hasn't
      // run yet, so nothing has declined anything.
      area.expansionMicSuppressed = false;

      // Switching expansion type discards any manual-add customisation so the new type's
      // placement algorithm runs cleanly. Re-customisation happens again on the next addExpansionMic.
      if (area.customized) area.customized.expansionMicrophones = false;

      // Leaving expansion entirely — whether by the user declining ('none') or by an automatic
      // reset to the un-prompted state (null) — also clears the manual opt-in flag, so a later
      // re-opt-in goes through the dialog flow again rather than silently inheriting prior state.
      // Previously gated on the literal 'none', which left the flag set on a null reset.
      if (type !== 'ceiling' && type !== 'table') area.expansionMicManualOptIn = false;

      // Display names for bar + expansion configurations come from a placeholder list
      // until the real DCM-X / DTM-X catalog entries land. See `expansionMicPlaceholders`
      // in `toolConstants/conferenceroomDesigner/constants.js`.
      const placeholders = toolIndex[state.currentTool].constants.expansionMicPlaceholders;
      // Captured in the ceiling branch below, applied AFTER revalidateUcxEligibility (see the
      // comment down there for why the ordering matters).
      let acousticsLimitedHeightToast: number | null = null;

      if (type === 'ceiling') {
        const hasTable = Boolean(area.table?.shape);
        // Set the default coverage mode only on the first ceiling selection
        // (when expansionMicCoverage is null — e.g., after mic-model change or after 'none').
        // Subsequent re-selections preserve the user's previous choice so that a
        // round-trip through Table doesn't silently flip the mode and make the bar
        // suddenly cover the (smaller) focused area, leaving zero expansion mics placed.
        if (!area.expansionMicCoverage) {
          area.expansionMicCoverage = { focused: hasTable, full_room: !hasTable };
        }
        // Default the expansion ceiling mic to dcm_x's own canonical height() (constants.js) —
        // flush with the ceiling, capped by the acoustics-optimal height for the current tier.
        // NOT `area.microphones.height`: the primary here is
        // always a non-CBC BAR (see the menu condition below), which mounts at table/wall
        // height, not ceiling — using the bar's height would default the ceiling expansion mic
        // to ~3'6" instead of near the ceiling. Editable afterward via the "Height"
        // dimensionInput (setExpansionMicrophones), same component as the primary ceiling
        // mic's Height field.
        const dcmConfig = toolIndex[state.currentTool].constants.equipment.microphones.dcm_x;
        const newCeilingHeight = dcmConfig?.height
          ? dcmConfig.height(area)
          : (area.ceilingHeight?.min ?? area.ceilingHeight ?? DEFAULT_CEILING_HEIGHT_M);
        // Seed with a real (silent, dry-run) PREDICTED layout rather than an empty `qty: 0,
        // layout: []` placeholder — `revalidateUcxEligibility` below reads this count to decide
        // Devio UCX capacity viability, and an empty layout is trivially "viable" (0 mics is
        // always within band '1-2'). Without this, the very FIRST ceiling-expansion selection in
        // an over-capacity room would sail through the capacity check, commit "Platform switched
        // to Android" (+ the acoustics-height toast below), and only fall back on a LATER
        // dispatch once the real placement lands — showing the user a platform-switch toast for
        // a design that was already known, from its own room geometry, to be non-viable.
        //
        // Uses real, renderable coords from `dryRunCeilingExpansionMicLayout` — NOT a blank
        // `Array.from({ length: n })` placeholder. This DOES render (a real render happens before
        // `calculateDevicesMiddleware`'s placement pass overwrites it moments later, unlike Case
        // 3a/3b's own throwaway `predictedArea`, which is check-only and never committed to real
        // state) — `MuiDevices` maps every device and reads fields like `.rotation` off each one,
        // so an array of `undefined` entries crashes the render outright.
        const predictedCeilingLayout = dryRunCeilingExpansionMicLayout(state, area, newCeilingHeight);
        area.expansionMicrophones = {
          model: 'dcm_x',
          modelName: placeholders.ceiling,
          qty: predictedCeilingLayout.qty,
          layout: predictedCeilingLayout.coords,
          height: newCeilingHeight,
        };
        // Informational toast when the room's acoustics is why this mic isn't mounted flush with
        // the ceiling. The height is correct from the moment it's set here, so there's no later
        // validation.js "adjusting height" correction to carry this information for the user
        // instead (see `computeCeilingMicHeightInfo`, constants.js).
        // Set AFTER `revalidateUcxEligibility` below (not here) — that call can ALSO set a
        // toast (e.g. "Platform switched to Android" the very first time a bar + expansion
        // design goes exclusive, which happens on this exact same dispatch) and would otherwise
        // silently overwrite this one since both write to the same `state._toastMessage`.
        if (dcmConfig?.isAcousticsLimited?.(area)) {
          acousticsLimitedHeightToast = newCeilingHeight;
        }
      } else if (type === 'table') {
        // Table expansion mic always runs focused coverage — matches the standalone TTM-X
        // config in constants.js (`ttm_x.coverage()` hardcodes `{ focused: true, full_room: false }`).
        // Tabletop mics inherently target the seating area around the table; there's no
        // meaningful full-room mode the way there is for ceiling mics.
        area.expansionMicCoverage = { focused: true, full_room: false };
        area.expansionMicrophones = {
          model: 'ttm_x',
          modelName: placeholders.table,
          qty: 0,
          layout: []
        };
      } else {
        area.expansionMicCoverage = null;
        area.expansionMicrophones = null;
      }
      revalidateUcxEligibility(state);
      // Both this AND revalidateUcxEligibility's own toast (e.g. "Platform switched to
      // Android...") can be true on the same dispatch — setOrQueueToast surfaces both rather
      // than letting one clobber the other (see its doc comment above).
      //
      // Guarded on `expansionMicType === 'ceiling'` STILL holding after revalidateUcxEligibility:
      // if the capacity fallback (Case 2) just fired, it cleared the ceiling expansion group
      // entirely (promoted to primary, `expansionMicType` → null) — the mic this toast describes
      // no longer exists in the design, so showing it alongside (or instead of) the "Bar has been
      // removed" toast would be stale and confusing. Case 3a (height-lowering) can't have fired
      // here — it only applies to an ALREADY-placed ceiling expansion mic with a real height to
      // lower, not this fresh `type === 'ceiling'` selection's brand-new empty layout.
      if (acousticsLimitedHeightToast !== null && area.expansionMicType === 'ceiling') {
        setOrQueueToast(
          state,
          'Ceiling expansion microphone is too high to pick up any talkers at given room acoustics. Adjusting microphone height to {height}.',
          'warning',
          acousticsLimitedHeightToast,
        );
      }
    },

    setExpansionMicCoverage: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      area.expansionMicCoverage = action.payload;
    },

    /**
     * Marks the area as having opted into expansion mics manually — i.e. via the
     * "Add expansion microphones" button in the menu, NOT via the auto-recommendation
     * dialog. The distinction matters because the manual path uses standard ceiling-mic
     * placement (grid distribution across the coverage box) instead of the gap-aware
     * `calcExpansionCeilingMicPlacement`, since no gap was detected — the user opted in
     * anyway and expects mics to appear.
     *
     * Coverage selection is left alone — the manual-add flow always pairs this dispatch
     * with `setExpansionMicType('ceiling' | 'table')` immediately after (see
     * ExpansionDialog), and that reducer seeds the focused/full_room default based on
     * table presence when coverage is null. Clearing coverage here would mask that
     * default (the seeding only fires when coverage is null) and leave the toggle in a
     * "neither selected" state — confusing for the user and the calc would silently
     * fall back to full_room via `getExpansionCeilingIsFocused`.
     */
    setExpansionMicManualOptIn: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      const value = typeof action.payload === 'boolean' ? action.payload : action.payload?.optedIn ?? true;
      area.expansionMicManualOptIn = value;
    },

    /**
     * Atomic placement reducer click-to-place path: user clicks "+",
     * then clicks the canvas before having picked an expansion type. The canvas click is
     * parked in `ui.pendingExpansionMicLocation`, the `expansionMicAdd` dialog opens, and
     * on confirm this reducer fires with the captured location.
     *
     * Single state transition (avoids the multi-dispatch race where `setExpansionMicType`
     * would trigger a recalc that overrides the user's click):
     *   - Sets `expansionMicType` (ceiling or table)
     *   - Initialises `expansionMicrophones` with the right model + a single-mic layout at
     *     the clicked location (TCM-X at ceiling height for ceiling; TTM-X at 0.91 m + horAngle=0 for table)
     *   - Sets `customized.expansionMicrophones = true` so subsequent recalcs preserve it
     *   - Clears `expansionMicCoverage = null` per the customization rule (toggle hides)
     *   - Clears `expansionMicManualOptIn` since we have a real placement, not the grid fallback
     */
    placeExpansionMicAtLocation: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      const { type, location } = action.payload ?? {};
      if ((type !== 'ceiling' && type !== 'table') || !location
          || typeof location.x !== 'number' || typeof location.y !== 'number') return;

      const placeholders = toolIndex[state.currentTool].constants.expansionMicPlaceholders;
      const isTable = type === 'table';
      // Ceiling expansion mic defaults to dcm_x's own canonical height() (flush with the
      // ceiling, capped for acoustics) — same fix as setExpansionMicType, not the primary bar's
      // mounting height (see setExpansionMicType for why not that).
      const dcmConfigForPlacement = toolIndex[state.currentTool].constants.equipment.microphones.dcm_x;
      const ceilingExpansionHeight = dcmConfigForPlacement?.height
        ? dcmConfigForPlacement.height(area)
        : (area.ceilingHeight?.min ?? area.ceilingHeight ?? DEFAULT_CEILING_HEIGHT_M);
      // Informational toast when acoustics is why this mic isn't mounted flush with the ceiling
      // — see the matching comment in setExpansionMicType above.
      // (No revalidateUcxEligibility call in this reducer, so no ordering concern here.)
      if (!isTable && dcmConfigForPlacement?.isAcousticsLimited?.(area)) {
        state._toastMessage = 'Ceiling expansion microphone is too high to pick up any talkers at given room acoustics. Adjusting microphone height to {height}.';
        state._toastHeightMeters = ceilingExpansionHeight;
        state._toastSeverity = 'warning';
      }
      const z = isTable ? TABLE_MIC_Z_HEIGHT : ceilingExpansionHeight;
      const newMic: any = {
        model: isTable ? 'ttm_x' : 'dcm_x',
        modelName: isTable ? placeholders.table : placeholders.ceiling,
        location: { x: location.x, y: location.y, z },
      };
      if (isTable) newMic.horAngle = 0;

      area.expansionMicType = type;
      area.expansionMicrophones = {
        model: newMic.model,
        modelName: newMic.modelName,
        qty: 1,
        layout: [newMic],
        ...(isTable ? {} : { height: ceilingExpansionHeight }),
      };
      area.expansionMicCoverage = null;
      area.expansionMicManualOptIn = false;

      if (!area.customized) area.customized = { ...DEFAULT_CUSTOMIZED };
      area.customized.expansionMicrophones = true;
    },

    setExpansionMicLayout: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionMicrophones) return;
      const requestedLayout = action.payload?.layout ?? [];

      // Previously truncated here against the UCC port budget (TC-05: "Tesira
      // has no fallback for a bar + expansion port-overflow"). That assumption
      // no longer holds: the Devio UCX capacity fallback (remove bar, swap
      // Platform/Family per androidExplicitlySelected once isUCXViable says
      // this layout exceeds Devio UCX capacity — spec §2.2/§2.3) replaces the
      // truncation, since silently dropping mics would under-provision the
      // room's actual coverage need. The full placement-computed layout is
      // accepted as-is; revalidateUcxEligibility below decides whether the
      // system needs to fall back.
      // TODO(step 2): call isUCXViable here (or from revalidateUcxEligibility)
      // once built, so an over-capacity layout triggers the bar-removal /
      // family-swap fallback instead of landing silently.
      const layout = requestedLayout;

      area.expansionMicrophones = {
        ...area.expansionMicrophones,
        layout,
        qty: layout.length,
      };
      // Mics landed, so the "placement declined" state no longer holds — clear it here rather
      // than relying on the middleware to remember, so the flag can never outlive the condition.
      // Only a non-empty layout clears it: an empty one is the very thing being recorded.
      if (layout.length > 0) area.expansionMicSuppressed = false;
      // The auto-placement mic count is exactly what Tree Frog port eligibility is
      // computed from — without this, a design that only overflows once the
      // calculated layout lands on its final count (dispatched here by
      // calculateDevicesMiddleware, not by a direct user click) stays on
      // productFamily='Devio' until something else happens to re-run
      // revalidateUcxEligibility (e.g. manually touching the Platform radio).
      revalidateUcxEligibility(state);
    },

    /**
     * Writes to `area.expansionMicrophones.coverageDensity` — currently the only
     * field this reducer accepts. The name mirrors the state slice it targets
     * (same convention as `setMicrophones` driving the primary-mic Coverage Density
     * fader), because the Fader atom resolves its read path via
     * `getStoreProp(dispatcher)` and won't find its own value back otherwise.
     *
     * Two callers:
     *   - Coverage Density fader (bar + manual-add + table expansion menu) dispatches
     *     `{ coverageDensity: { value: N } }` when the user drags the slider.
     *   - Middleware echoes back the computed density `{ value, jumpPoints, overlaps }`
     *     after the path-based density calc runs, with `_fromSyncMiddleware` set so
     *     the recalc trigger skips its own dispatch.
     *
     * Deep-merges `coverageDensity` so the slider's partial update (just `value`)
     * preserves the middleware-populated `jumpPoints` / `overlaps` instead of
     * shallow-replacing them.
     *
     * Also accepts `height` — the ceiling expansion mic's (dcm_x) mounting height,
     * written by the "Height" dimensionInput (same component as the primary ceiling
     * mic's Height field). Clamped via `dcm_x.heightRange(area)` like the primary mic
     * and `setExpansionSpeakers`' pendant height field, and mirrored onto every device
     * in `expansionMicrophones.layout` so existing placements move with the new height.
     *
     * Payload fields other than `coverageDensity` and `height` (and the transit-only
     * `_fromSyncMiddleware`) are intentionally ignored. If a future caller needs to
     * write `model` / `modelName` / `layout` / `qty`, add an explicit branch here
     * (or a dedicated reducer) rather than widening the spread — keeps the surface
     * narrow so the action can't accidentally clobber unrelated fields.
     */
    setExpansionMicrophones: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionMicrophones) return;
      const { coverageDensity, height, ceilingHeight } = action.payload ?? {};
      // Captured here, applied AFTER revalidateUcxEligibility(state) below — that call can set
      // its own state._toastMessage (e.g. a platform-switch notice), which would otherwise
      // silently clobber this one since it runs later in the same dispatch. See the identical
      // pattern (and its rationale) in setExpansionMicType above.
      let acousticsLimitedHeightToast: number | null = null;
      // Same deferred treatment as acousticsLimitedHeightToast above: raised when the user
      // tries to push the expansion mic above the ceiling, mirroring the primary mic's e4
      // check in validation.js. Holds the ceiling height in metres for the {height} placeholder.
      let aboveCeilingToast: number | null = null;
      if (coverageDensity !== undefined) {
        area.expansionMicrophones = {
          ...area.expansionMicrophones,
          coverageDensity: {
            ...(area.expansionMicrophones.coverageDensity ?? {}),
            ...coverageDensity,
          },
        };
      }
      // Expansion CEILING mic (dcm_x) follows its own canonical height() (flush with the
      // ceiling, capped for acoustics) on every ceiling-height change, exactly
      // like the primary ceiling mic (tcm_x/bma360d) — see setMicrophones' `'ceilingHeight' in
      // newMic` branch, which FloorplanControls.tsx's ceiling-height handler already dispatches
      // for the primary mic (before this reducer, in the same handler — so `area.ceilingHeight`
      // is already the new value here too). Before this, expansionMicrophones.height only got a
      // ONE-TIME snapshot of ceilingHeight.min when the user first switched to ceiling expansion
      // (setExpansionMicType) and never moved again afterward — a later ceiling height edit
      // left it stuck at the old value while the primary mic (and both speaker paths, PIG-612)
      // correctly followed. Table expansion (ttm_x) isn't ceiling-mounted, so this is a no-op
      // for that type — guarded on expansionMicType rather than model to stay correct even if
      // a future non-dcm_x ceiling model is added.
      if (ceilingHeight !== undefined && area.expansionMicType === 'ceiling') {
        const dcmConfig = toolIndex[state.currentTool].constants.equipment.microphones[area.expansionMicrophones.model];
        const newCeilingSyncHeight = dcmConfig?.height ? dcmConfig.height(area) : ceilingHeight;
        area.expansionMicrophones = {
          ...area.expansionMicrophones,
          height: newCeilingSyncHeight,
          layout: (area.expansionMicrophones.layout ?? []).map((m: any) => ({
            ...m,
            location: { ...m.location, z: newCeilingSyncHeight },
          })),
        };
        // Informational toast when acoustics is why this mic isn't mounted flush with the
        // ceiling — see the matching comment in setExpansionMicType above.
        if (dcmConfig?.isAcousticsLimited?.(area)) {
          acousticsLimitedHeightToast = newCeilingSyncHeight;
        }
      }
      if (height !== undefined) {
        const equipment = toolIndex[state.currentTool]?.constants?.equipment?.microphones?.[area.expansionMicrophones.model];
        // Snap BEFORE clamping, mirroring setMicrophones' manual-height branch — the
        // expansion ceiling mic (dcm_x) is in POLE_MOUNT and mounts on the same 0.5' pole
        // sections as the primary TCM-X, so a free-form height here would be unbuildable.
        // Without this the spinner's 0.5' step would also drift off-grid instead of walking it.
        const snappedHeight = micInCategory(area.expansionMicrophones.model, 'POLE_MOUNT')
          ? snapToPoleIncrement(height)
          : height;
        const clampedHeight = equipment?.heightRange
          ? clamp(snappedHeight, ...(equipment.heightRange(area) as [number, number]))
          : snappedHeight;
        // Above-ceiling feedback, matching the primary mic's e4 branch in validation.js
        // (`Microphone height cannot exceed <ceiling> (ceiling height)`). Without this the
        // expansion field silently clamped, so pressing up at the ceiling looked as dead as
        // the down-arrow bug this change fixes. Tested against the RAW payload, before the
        // pole snap and clamp — the same pre-clamp value the primary path stashes in
        // `_originalHeightInput` — so a snap that lands exactly on the ceiling isn't reported
        // as exceeding it. The 1mm tolerance matches validation.js's `heightTolerance` and
        // absorbs ft→m conversion error at exactly-ceiling heights.
        const ceilingLimit = area.ceilingHeight?.min ?? area.ceilingHeight;
        if (
          area.expansionMicType === 'ceiling' &&
          typeof ceilingLimit === 'number' &&
          height > ceilingLimit + 0.001
        ) {
          aboveCeilingToast = ceilingLimit;
        }
        area.expansionMicrophones = {
          ...area.expansionMicrophones,
          height: clampedHeight,
          layout: (area.expansionMicrophones.layout ?? []).map((m: any) => ({
            ...m,
            location: { ...m.location, z: clampedHeight },
          })),
        };
      }
      revalidateUcxEligibility(state);
      // Guarded on `expansionMicType === 'ceiling'` STILL holding after revalidateUcxEligibility —
      // see the matching comment in setExpansionMicType above: if Case 2 just fired, the ceiling
      // expansion group this toast describes no longer exists (cleared/promoted to primary), so
      // showing it would be stale alongside "Bar has been removed".
      if (acousticsLimitedHeightToast !== null && area.expansionMicType === 'ceiling') {
        setOrQueueToast(
          state,
          'Ceiling expansion microphone is too high to pick up any talkers at given room acoustics. Adjusting microphone height to {height}.',
          'warning',
          acousticsLimitedHeightToast,
        );
      }
      // Wording is deliberately IDENTICAL to the primary mic's e4 message in validation.js so
      // the two height fields read the same. toastMiddleware substitutes {height} with
      // distanceToString() in the user's display unit — the same formatting validation.js
      // applies inline — so the resolved text matches in both feet and metres.
      // Same post-revalidate `expansionMicType === 'ceiling'` guard as the toast above.
      if (aboveCeilingToast !== null && area.expansionMicType === 'ceiling') {
        setOrQueueToast(
          state,
          'Microphone height cannot exceed {height} (ceiling height)',
          'error',
          aboveCeilingToast,
        );
      }
    },

    /**
     * Manually appends one expansion microphone at the given location and marks the
     * expansion layout as customized so subsequent recalculations (calcExpansion*MicPlacement)
     * preserve the user's manual mic instead of overwriting it.
     *
     * Used by the action bar's "add" handler when the user has a non-CBC bar mic + an
     * already-configured expansion type ('ceiling' or 'table'). Type-agnostic — pulls the
     * model / modelName from `area.expansionMicrophones`, which the `setExpansionMicType`
     * reducer keeps in sync with the active type.
     */
    addExpansionMic: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionMicrophones) return;
      const { location } = action.payload ?? {};
      if (!location || typeof location.x !== 'number' || typeof location.y !== 'number') return;

      // Cap enforcement — `constants.maxExpansionMics` (4 today). The action condition
      // is the user-facing "Add disabled at cap" surface; this guard is defence-in-depth
      // against rapid double-clicks that race the condition update.
      const maxCap = toolIndex[state.currentTool]?.constants?.maxExpansionMics ?? Infinity;
      const currentCount = area.expansionMicrophones.layout?.length ?? 0;
      if (currentCount >= maxCap) return;

      // TC-05: Tesira has no fallback for a bar + expansion port-overflow —
      // refuse the add once it would exceed the UCC port budget given the
      // area's current expansion speaker count, rather than letting the
      // design overflow and swapping Platform/Family/mic hardware.
      if (currentCount >= maxExpansionMicCountFor(area, maxCap)) {
        state._toastMessage = 'Adding another microphone would exceed the UCC peripheral port budget for this bar + expansion design.';
        state._toastSeverity = 'warning';
        return;
      }

      const isTable = area.expansionMicType === 'table';
      // Ceiling expansion mics mount at the group's configured height — user-editable via
      // the Height field, defaulting to flush ceiling height on creation (setExpansionMicType).
      const z = isTable
        ? TABLE_MIC_Z_HEIGHT
        : (area.expansionMicrophones.height ?? area.ceilingHeight?.min ?? DEFAULT_CEILING_HEIGHT_M);
      const newMic: any = {
        model: area.expansionMicrophones.model,
        modelName: area.expansionMicrophones.modelName,
        location: { x: location.x, y: location.y, z },
      };
      if (isTable) newMic.horAngle = 0;

      area.expansionMicrophones = {
        ...area.expansionMicrophones,
        layout: [...(area.expansionMicrophones.layout ?? []), newMic],
        qty: (area.expansionMicrophones.qty ?? 0) + 1,
      };

      // Customisation invalidates the focused / full_room preset — the user's hand-placed
      // layout no longer matches either coverage box. Clear the toggle state to null (stashing
      // the previous value so undo can restore it) so the menu condition
      // (`expansionMicCoverage !== null`) hides the selector. Skipped for table type
      // because the toggle isn't shown for table at all.
      if (area.expansionMicType !== 'table') {
        clearExpansionCoverage(area, 'expansionMicCoverage');
      }

      if (!area.customized) area.customized = { ...DEFAULT_CUSTOMIZED };
      area.customized.expansionMicrophones = true;
      revalidateUcxEligibility(state);
    },

    /**
     * Removes a single expansion microphone by its index in `area.expansionMicrophones.layout`.
     * Preserves the type ('ceiling' or 'table') and keeps `customized.expansionMicrophones`
     * true so the placement algorithm doesn't repopulate the deleted slot on the next recalc.
     *
     * Ceiling expansion mics are mirrored into `area.microphones.layout` via getDeviceLayouts'
     * `allMicCoords = [...micCoords, ...expansionCoords]`. Removing only from
     * `expansionMicrophones.layout` would leave the mirrored copy behind, and the
     * MuiMicrophones "split tcm_x out for square icon" render path would keep showing it.
     * So we also splice the matching entry out of `microphones.layout` by (model, x, y).
     * For table mics this filter is a no-op (ttm_x entries never land in microphones.layout).
     *
     * Used by the action bar's 'delete' dispatcher when the user clicks an individual
     * expansion mic with delete mode active.
     */
    removeExpansionMic: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionMicrophones) return;
      const layout = area.expansionMicrophones.layout ?? [];
      const { index } = action.payload ?? {};
      if (typeof index !== 'number' || index < 0 || index >= layout.length) return;

      const removed = layout[index];
      const nextLayout = layout.filter((_: any, i: number) => i !== index);
      area.expansionMicrophones = {
        ...area.expansionMicrophones,
        layout: nextLayout,
        qty: nextLayout.length,
      };

      if (removed && Array.isArray(area.microphones?.layout)) {
        // getDeviceLayouts builds microphones.layout as [primary mics, ...ceiling expansion mics]
        // in the same order as expansionMicrophones.layout (allMicCoords concatenation in
        // roomCalculations.ts). Splice by mirrored index instead of (model,x,y) equality so two
        // expansion mics that happen to land at identical coordinates — e.g. rapid double-add at
        // the same click pixel — don't both get removed when the user only asked to delete one.
        // Table expansion mics never mirror into microphones.layout, so this is a no-op there:
        // primaryCount === layout.length and mirroredIdx falls past the array.
        const primaryCount = area.microphones.layout.filter((m: any) => m.model !== removed.model).length;
        const mirroredIdx = primaryCount + index;
        if (mirroredIdx >= 0 && mirroredIdx < area.microphones.layout.length) {
          // Defensive guard against the [primary, ...expansion] ordering invariant: if the
          // entry we're about to splice doesn't match the removed model, getDeviceLayouts'
          // concatenation order has changed and a blind splice would corrupt a primary mic.
          // Skip the splice rather than silently mutating the wrong entry.
          if (area.microphones.layout[mirroredIdx].model === removed.model) {
            area.microphones.layout.splice(mirroredIdx, 1);
          }
        }
      }

      // When the user deletes the LAST expansion mic, transition the area out of expansion
      // mode entirely. Set the type to 'none' (not null) so it matches the "user declined"
      // path used by the menu condition — the type selector hides, the menu button flips
      // from "Remove" back to "Add", and the user can re-engage anytime by clicking Add
      // (which opens the dialog and sets the type back to a real value).
      if (nextLayout.length === 0) {
        area.expansionMicType = 'none';
        clearExpansionCoverage(area, 'expansionMicCoverage');
        area.expansionMicrophones = null;
        if (area.customized) area.customized.expansionMicrophones = false;
        revalidateUcxEligibility(state);
        return;
      }

      // Non-last delete: customisation invalidates the focused / full_room preset, so clear
      // the toggle state to null (stashing the previous value for undo) and the menu
      // condition hides the selector (parallel to addExpansionMic). Skipped for table type
      // because the toggle isn't shown for table.
      if (area.expansionMicType !== 'table') {
        clearExpansionCoverage(area, 'expansionMicCoverage');
      }

      if (!area.customized) area.customized = { ...DEFAULT_CUSTOMIZED };
      area.customized.expansionMicrophones = true;
      revalidateUcxEligibility(state);
    },

    setExpansionSpeakerType: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      const requested = typeof action.payload === 'string' ? action.payload : action.payload?.type;
      // Bar + expansion designs only support the C-IC6 family (Tree Frog port
      // accounting spec §1) — pendant expansion speakers are no longer offered, so
      // a 'pendant' request resolves to ceiling. Not gated on isDevioUcxDesign: any
      // bar design that gets expansion mics/speakers is forced onto Android/Devio
      // UCX by revalidateUcxEligibility (exclusive bar shape), and the first type
      // pick on a Windows bar design happens BEFORE that switch — a platform check
      // here would let pendant through onto what is about to become a UCX design.
      const type = requested === 'pendant' ? 'ceiling' : requested;
      area.expansionSpeakerType = type ?? null;

      if (type === 'ceiling') {
        const hasTable = Boolean(area.table?.shape);
        // Seed the default coverage mode only on the first ceiling selection — same
        // pattern as setExpansionMicType. Default to focused when a table exists (the
        // coverage box clusters around the table), full_room otherwise. Preserves the
        // user's prior choice on a round-trip through pendant or none.
        if (!area.expansionSpeakerCoverage) {
          area.expansionSpeakerCoverage = { focused: hasTable, full_room: !hasTable };
        }
        area.expansionSpeakers = {
          model: 'ceilingspkr',
          modelName: 'Desono C-IC6',
          qty: 0,
          layout: [],
        };
      } else {
        area.expansionSpeakerCoverage = null;
        area.expansionSpeakers = null;
      }
      revalidateUcxEligibility(state);
    },

    /**
     * Updates the chosen expansion speaker model (e.g. swap C-IC6 for a different
     * ceiling speaker variant). Looks up the modelName from the equipment catalog so
     * the menu textInput / equipment list / placement code all see a consistent label.
     * Layout/qty are wiped because the placement is model-dependent — the next recalc
     * will repopulate them.
     */
    setExpansionSpeakerModel: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionSpeakers) return;
      const model = typeof action.payload === 'string' ? action.payload : action.payload?.model;
      if (!model) return;
      const equipment = toolIndex[state.currentTool]?.constants?.equipment?.speakers?.[model];
      area.expansionSpeakers = {
        ...area.expansionSpeakers,
        model,
        modelName: equipment?.modelName ?? area.expansionSpeakers.modelName,
        qty: 0,
        layout: [],
        // Re-seed the mounting height from the new model (pendant speakers hang below the
        // ceiling); models without a height function drop the field back to undefined.
        ...(equipment?.height
          ? { height: equipment.height(area) }
          : { height: undefined }),
      };
    },

    setExpansionSpeakerCoverage: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area) return;
      area.expansionSpeakerCoverage = action.payload;
    },

    /**
     * Updates fields on the expansion-speaker slot from the menu (currently the pendant
     * Height input). Mirrors the primary `setSpeakers` height handling: clamp to the model's
     * valid range and push the new Z into any placed speakers so tooltips/plots stay in sync.
     */
    setExpansionSpeakers: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionSpeakers) return;
      const payload = action.payload ?? {};
      const next: any = { ...area.expansionSpeakers, ...payload };

      if ('height' in payload) {
        const equipment = toolIndex[state.currentTool]?.constants?.equipment?.speakers?.[next.model];
        if (equipment?.heightRange) {
          const [minH, maxH] = equipment.heightRange(area);
          next.height = clamp(payload.height, minH, maxH);
        }
        if (Array.isArray(next.layout)) {
          next.layout = next.layout.map((s: any) => ({ ...s, location: { ...s.location, z: next.height } }));
        }
      }

      area.expansionSpeakers = next;
    },

    setExpansionSpeakerLayout: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionSpeakers) return;
      const requestedLayout = action.payload?.layout ?? [];

      // Previously truncated here against the UCC port budget (same TC-05
      // reasoning as setExpansionMicLayout above). Superseded by the Devio UCX
      // capacity fallback (spec §2.2/§2.3, isUCXViable) — truncating a
      // coverage-driven speaker count would under-provision the room, so the
      // full placement-computed layout is accepted as-is and
      // revalidateUcxEligibility decides whether the system needs to fall
      // back.
      // TODO(step 2): call isUCXViable here (or from revalidateUcxEligibility)
      // once built.
      const layout = requestedLayout;

      area.expansionSpeakers = {
        ...area.expansionSpeakers,
        layout,
        qty: layout.length,
      };
      // Same reasoning as setExpansionMicLayout above: the auto-placement
      // speaker count is exactly what Tree Frog port/amp-capacity eligibility
      // is computed from (e.g. pendant speakers exceeding DCM-XA capacity) —
      // without this, productFamily stays stuck on 'Devio' until something
      // else re-triggers revalidateUcxEligibility.
      revalidateUcxEligibility(state);
    },

    /**
     * Manually appends one expansion speaker at the given location and marks the
     * expansion speaker layout as customised so `getDeviceLayouts` preserves the user's
     * manual placement instead of overwriting it with `calcExpansionSpeakerPlacement`.
     *
     * Mirrors `addExpansionMic` for ceiling expansion speakers (the only supported
     * mounting today; pendant placement uses the same canvas flow). Z-height comes
     * from `area.ceilingHeight.min` since expansion speakers ceiling-mount.
     *
     * Cap enforcement: respects `constants.maxExpansionSpeakers` so a runaway click loop
     * can't overflow the layout. Returns silently when the cap is reached — the action
     * dispatcher's condition is the user-facing "Add disabled at cap" surface.
     */
    addExpansionSpeaker: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionSpeakers) return;
      const { location } = action.payload ?? {};
      if (!location || typeof location.x !== 'number' || typeof location.y !== 'number') return;

      const maxCap = toolIndex[state.currentTool]?.constants?.maxExpansionSpeakers ?? Infinity;
      const currentCount = area.expansionSpeakers.layout?.length ?? 0;
      if (currentCount >= maxCap) return;

      // TC-05: Tesira has no fallback for a bar + expansion port-overflow —
      // refuse the add once it would exceed the UCC port budget given the
      // area's current expansion mic count, rather than letting the design
      // overflow and swapping Platform/Family/mic hardware.
      if (currentCount >= maxExpansionSpeakerCountFor(area, maxCap)) {
        state._toastMessage = 'Adding another speaker would exceed the UCC peripheral port budget for this bar + expansion design.';
        state._toastSeverity = 'warning';
        return;
      }

      const z = area.ceilingHeight?.min ?? DEFAULT_CEILING_HEIGHT_M;
      const newSpkr: any = {
        model: area.expansionSpeakers.model,
        modelName: area.expansionSpeakers.modelName,
        location: { x: location.x, y: location.y, z },
      };

      area.expansionSpeakers = {
        ...area.expansionSpeakers,
        layout: [...(area.expansionSpeakers.layout ?? []), newSpkr],
        qty: (area.expansionSpeakers.qty ?? 0) + 1,
      };

      // Customisation invalidates the focused / full_room preset — the user's hand-placed
      // layout no longer matches either coverage box. Clear the toggle state to null (stashing
      // the previous value so undo can restore it) so the menu condition
      // (`expansionSpeakerCoverage !== null`) hides the selector. Skipped for pendant
      // because the toggle isn't shown for pendant at all.
      if (area.expansionSpeakerType !== 'pendant') {
        clearExpansionCoverage(area, 'expansionSpeakerCoverage');
      }

      if (!area.customized) area.customized = { ...DEFAULT_CUSTOMIZED };
      area.customized.expansionSpeakers = true;
      revalidateUcxEligibility(state);
    },

    /**
     * Removes a single expansion speaker by its index in `area.expansionSpeakers.layout`.
     * Preserves the type ('ceiling' or 'pendant') and keeps `customized.expansionSpeakers`
     * true so the placement algorithm doesn't repopulate the deleted slot on the next recalc.
     *
     * Unlike `removeExpansionMic`, no mirror cleanup is required — expansion speakers
     * render from their own slot only (see MuiSpeakers in MuiDevices.tsx), they don't
     * concatenate into `area.speakers.layout`.
     *
     * When the user deletes the LAST expansion speaker, exit expansion-speaker mode
     * entirely (mirrors removeExpansionMic's "last mic removed" branch): type → 'none',
     * slot → null, customised flag cleared. The user can re-engage via the menu's
     * Expansion speaker type dropdown.
     */
    removeExpansionSpeaker: (state, action) => {
      const area = state.areas[state.activeTab];
      if (!area || !area.expansionSpeakers) return;
      const layout = area.expansionSpeakers.layout ?? [];
      const { index } = action.payload ?? {};
      if (typeof index !== 'number' || index < 0 || index >= layout.length) return;

      const nextLayout = layout.filter((_: any, i: number) => i !== index);
      area.expansionSpeakers = {
        ...area.expansionSpeakers,
        layout: nextLayout,
        qty: nextLayout.length,
      };

      if (nextLayout.length === 0) {
        area.expansionSpeakerType = 'none';
        clearExpansionCoverage(area, 'expansionSpeakerCoverage');
        area.expansionSpeakers = null;
        if (area.customized) area.customized.expansionSpeakers = false;
        revalidateUcxEligibility(state);
        return;
      }

      // Non-last delete: customisation invalidates the focused / full_room preset.
      // Clear the toggle state to null (stashing the previous value for undo) and the
      // menu condition hides the selector (parallel to addExpansionSpeaker). Skipped for
      // pendant (toggle not shown).
      if (area.expansionSpeakerType !== 'pendant') {
        clearExpansionCoverage(area, 'expansionSpeakerCoverage');
      }

      if (!area.customized) area.customized = { ...DEFAULT_CUSTOMIZED };
      area.customized.expansionSpeakers = true;
      revalidateUcxEligibility(state);
    },

  },
}

export default roomSlice;

// Calculation functions and selectors — extracted to roomCalculations.ts for maintainability.
// NOTE: Use explicit import-then-export (not "export { X } from 'Y'") so webpack can
// statically resolve these exports even when room.ts is inside a circular dependency.
import {
  getCoverageBoxes,
  createDeviceAdjutmentAndAim,
  getDeviceLayouts,
  getTileQuantities,
  calculateBarMicPlotData,
  calculagteCeilingRelativePloptData,
  calculateWallRelativePlotData,
} from 'reduxModules/calculations/roomCalculations';
export {
  getCoverageBoxes,
  createDeviceAdjutmentAndAim,
  getDeviceLayouts,
  getTileQuantities,
  calculateBarMicPlotData,
  calculateWallSpeakerTargetPoint,
  calculagteCeilingRelativePloptData,
  calculateWallRelativePlotData,
};
